import { trpcClient as trpc } from '@/api/trpc.ts';
import { convertEstTimestampToTradingView, convertTradingViewTimestampToEst } from '@/utils/dateHelpers/dateHelpers.ts';
import {
    ChartPatternAnchorRole,
    type ChartPatternDisplayMode,
    ChartPatternEventCategory,
    type BarOHLCVDetailed,
    type ChartPatternAnchor,
    type ChartPatternResolvedEvent,
    type ChartPatternResolvedOccurrence,
    type ChartPatternResolvedSnapshot,
    type ChartPatternState,
    type ChartPatternSummary,
    type ChartLine,
    type ChartTextItem,
    type HexColor,
    type PriceLine,
    type HtmlColor,
    type KeyLevelItem,
    type MicrotrendApiObject,
    type SupplyDemandZone,
    type Trendline,
    type ChartMarker,
    type UnixTimestamp,
    type PriceTrajectoryCondensed
} from '@einhaus/project314-types';

import type { ChartSettings, IndicatorInputWithChartSettings } from './ChartContainer.tsx';
import {
    convertBarsToTradingView,
    convertDurationIndicatorBarsToTradingView,
    createLevelLineDataFromBars,
    createTradingViewVolumeFromBars,
    createTrendlineDataFromBars
} from '@/utils/barHelpers.ts';
import { getUnixTime, getUnixTimePlusMinus, getDate, getDateFromUnix, last } from '@einhaus/meteoric-helpers';
import { KEY_LEVEL_SUBTYPES } from '@/config/constants.ts';
import { CANDLESTICK_PATTERN_SUBTYPES, DEFAULT_COMPARISON_LOOKBACK_BARS, getRandomHtmlColor } from '@einhaus/project314-shared';
import { arrayMedian } from '@/utils/mathHelpers.ts';
import type { inferProcedureOutput } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';
import {
    type IChartApi,
    type IPrimitivePaneRenderer,
    type IPrimitivePaneView,
    type ISeriesApi,
    type ISeriesPrimitive,
    type PrimitivePaneViewZOrder,
    type SeriesAttachedParameter,
    type SeriesMarker,
    type Time,
    type SingleValueData,
    type BarData,
    type UTCTimestamp,
    createChart,
    CrosshairMode,
    HistogramSeries,
    CandlestickSeries,
    LineSeries,
    LineStyle,
    createSeriesMarkers
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import {
    getChartPatternEventsForDisplay,
    getChartPatternHistoricalEventsForDisplay,
    getChartPatternHistoricalOccurrencesForDisplay,
    getChartPatternSnapshotSeriesForDisplay,
    getChartPatternSnapshotsForDisplay,
    isChartPatternDurationSupported
} from './chartPatternDisplayHelpers.ts';

const LINE_WIDTH_PIXELS = 2;

const CHART_PATTERN_STATE_COLORS: Record<ChartPatternState, string> = {
    forming: '#60A5FA',
    formed: '#F59E0B',
    triggered: '#22C55E',
    successful: '#15803D',
    failed: '#EF4444',
    expired: '#6B7280'
};

const BELOW_BAR_CHART_PATTERN_ANCHORS: Set<ChartPatternAnchorRole> = new Set([
    ChartPatternAnchorRole.cupLow,
    ChartPatternAnchorRole.handleLow,
    ChartPatternAnchorRole.lowerStart,
    ChartPatternAnchorRole.lowerEnd,
    ChartPatternAnchorRole.low1,
    ChartPatternAnchorRole.low2,
    ChartPatternAnchorRole.trough1,
    ChartPatternAnchorRole.trough2,
    ChartPatternAnchorRole.flagLow
]);

const CHART_PATTERN_EVENT_COLORS: Record<ChartPatternEventCategory, string> = {
    [ChartPatternEventCategory.structureFormed]: '#F59E0B',
    [ChartPatternEventCategory.breakoutReady]: '#60A5FA',
    [ChartPatternEventCategory.breakoutConfirmed]: '#22C55E',
    [ChartPatternEventCategory.failed]: '#EF4444',
    [ChartPatternEventCategory.expired]: '#6B7280'
};

const CHART_PATTERN_SHORT_LABELS: Record<string, string> = {
    doubleBottom: 'DB',
    doubleTop: 'DT',
    cupHandle: 'CwH',
    bullFlag: 'BF',
    bearFlag: 'BrF',
    headShoulders: 'H&S',
    inverseHeadShoulders: 'IH&S',
    risingWedge: 'RW',
    fallingWedge: 'FW',
    ascendingTriangle: 'AscTri',
    descendingTriangle: 'DescTri',
    symmetricalTriangle: 'SymTri'
};

const CHART_PATTERN_ANCHOR_SHORT_LABELS: Record<ChartPatternAnchorRole, string> = {
    [ChartPatternAnchorRole.leftLip]: 'LL',
    [ChartPatternAnchorRole.cupLow]: 'Cup',
    [ChartPatternAnchorRole.rightLip]: 'RL',
    [ChartPatternAnchorRole.handleLow]: 'Handle',
    [ChartPatternAnchorRole.breakout]: 'BO',
    [ChartPatternAnchorRole.upperStart]: 'US',
    [ChartPatternAnchorRole.upperEnd]: 'UE',
    [ChartPatternAnchorRole.lowerStart]: 'LS',
    [ChartPatternAnchorRole.lowerEnd]: 'LE',
    [ChartPatternAnchorRole.high1]: 'H1',
    [ChartPatternAnchorRole.high2]: 'H2',
    [ChartPatternAnchorRole.low1]: 'L1',
    [ChartPatternAnchorRole.low2]: 'L2',
    [ChartPatternAnchorRole.neckline]: 'NL',
    [ChartPatternAnchorRole.poleStart]: 'Pole 1',
    [ChartPatternAnchorRole.poleEnd]: 'Pole 2',
    [ChartPatternAnchorRole.flagHigh]: 'FH',
    [ChartPatternAnchorRole.flagLow]: 'FL',
    [ChartPatternAnchorRole.leftShoulder]: 'LS',
    [ChartPatternAnchorRole.head]: 'Head',
    [ChartPatternAnchorRole.rightShoulder]: 'RS',
    [ChartPatternAnchorRole.trough1]: 'T1',
    [ChartPatternAnchorRole.trough2]: 'T2'
};

const CHART_PATTERN_EVENT_SHORT_LABELS: Record<ChartPatternEventCategory, string> = {
    [ChartPatternEventCategory.structureFormed]: 'Formed',
    [ChartPatternEventCategory.breakoutReady]: 'Ready',
    [ChartPatternEventCategory.breakoutConfirmed]: 'Breakout',
    [ChartPatternEventCategory.failed]: 'Failed',
    [ChartPatternEventCategory.expired]: 'Expired'
};

const CHART_PATTERN_PHASE_SHORT_LABELS: Record<string, string> = {
    secondBottomForming: 'Forming',
    secondTopForming: 'Forming',
    channelBuilding: 'Forming',
    trendlineBuilding: 'Forming',
    breakoutReady: 'Ready',
    breakdownReady: 'Ready',
    postBreakout: 'Breakout',
    postBreakdown: 'Breakdown',
    failed: 'Failed',
    expired: 'Expired'
};

const CHART_PATTERN_LEVEL_MERGE_TOLERANCE_PCT = 0.006;
const MAIN_CHART_RIGHT_OFFSET_BARS = 4;
const SUPPLY_DEMAND_ZONE_RENDER_BAR_SPAN = 200;
const MAX_SUPPLY_DEMAND_ZONES_TO_RENDER = 60;
const HIGHER_INTRADAY_SUPPLY_DEMAND_DURATIONS = new Set(['60m', '120m', '180m', '240m']);
const MAX_HIGHER_INTRADAY_SUPPLY_DEMAND_ZONES_PER_SIDE = 3;
const HIGHER_INTRADAY_SUPPLY_DEMAND_MAX_DISTANCE_PCT = 0.08;

// Marker sources for consolidated marker management
type MarkerSource = 'strategy-entries' | 'highs-lows' | 'candlestick-patterns' | 'supply-demand' | 'chart-patterns';

type ChartPatternFlagGeometry = {
    poleEndPrice: number;
    poleEndTimestamp: number;
    poleStartPrice: number;
    poleStartTimestamp: number;
    flagEndLowerPrice: number;
    flagEndTimestamp: number;
    flagEndUpperPrice: number;
    flagStartLowerPrice: number;
    flagStartTimestamp: number;
    flagStartUpperPrice: number;
};

type ChartPatternBoundaryGeometry = {
    lowerEndPrice: number;
    lowerEndTimestamp: number;
    lowerStartPrice: number;
    lowerStartTimestamp: number;
    upperEndPrice: number;
    upperEndTimestamp: number;
    upperStartPrice: number;
    upperStartTimestamp: number;
};

type ChartPatternSegmentGeometry = {
    endPrice: number;
    endTimestamp: number;
    startPrice: number;
    startTimestamp: number;
};

type ChartPatternProjectedBoundaryWindow = {
    endTimestamp: number;
    startTimestamp: number;
};

type ChartPatternBoundaryFillShape = {
    endTimestamp: number;
    fillColor: string;
    lowerEndPrice: number;
    lowerStartPrice: number;
    startTimestamp: number;
    upperEndPrice: number;
    upperStartPrice: number;
};

class ChartPatternBoundaryFillRenderer implements IPrimitivePaneRenderer {
    constructor(private readonly primitive: ChartPatternBoundaryFillPrimitive) {}

    draw(target: CanvasRenderingTarget2D): void {
        this.primitive.draw(target);
    }
}

class ChartPatternBoundaryFillPaneView implements IPrimitivePaneView {
    private readonly rendererInstance: ChartPatternBoundaryFillRenderer;

    constructor(private readonly primitive: ChartPatternBoundaryFillPrimitive) {
        this.rendererInstance = new ChartPatternBoundaryFillRenderer(primitive);
    }

    renderer(): IPrimitivePaneRenderer | null {
        return this.primitive.hasRenderableShape() ? this.rendererInstance : null;
    }

    zOrder(): PrimitivePaneViewZOrder {
        return 'normal';
    }
}

class ChartPatternBoundaryFillPrimitive implements ISeriesPrimitive<Time> {
    private readonly paneViewsArray: readonly IPrimitivePaneView[];
    private attachedChart?: SeriesAttachedParameter<Time>['chart'];
    private attachedSeries?: SeriesAttachedParameter<Time>['series'];
    private readonly paneView: ChartPatternBoundaryFillPaneView;

    constructor(private readonly shape: ChartPatternBoundaryFillShape) {
        this.paneView = new ChartPatternBoundaryFillPaneView(this);
        this.paneViewsArray = [this.paneView];
    }

    attached(param: SeriesAttachedParameter<Time>): void {
        this.attachedChart = param.chart;
        this.attachedSeries = param.series;
    }

    detached(): void {
        this.attachedChart = undefined;
        this.attachedSeries = undefined;
    }

    paneViews(): readonly IPrimitivePaneView[] {
        return this.paneViewsArray;
    }

    hasRenderableShape(): boolean {
        return this.attachedChart != null && this.attachedSeries != null;
    }

    draw(target: CanvasRenderingTarget2D): void {
        if (!this.attachedChart || !this.attachedSeries) {
            return;
        }

        const startX = this.attachedChart.timeScale().timeToCoordinate(convertEstTimestampToTradingView(this.shape.startTimestamp));
        const endX = this.attachedChart.timeScale().timeToCoordinate(convertEstTimestampToTradingView(this.shape.endTimestamp));
        const upperStartY = this.attachedSeries.priceToCoordinate(this.shape.upperStartPrice);
        const upperEndY = this.attachedSeries.priceToCoordinate(this.shape.upperEndPrice);
        const lowerEndY = this.attachedSeries.priceToCoordinate(this.shape.lowerEndPrice);
        const lowerStartY = this.attachedSeries.priceToCoordinate(this.shape.lowerStartPrice);

        if (startX == null || endX == null || upperStartY == null || upperEndY == null || lowerEndY == null || lowerStartY == null) {
            return;
        }

        target.useMediaCoordinateSpace(({ context }) => {
            context.save();
            context.fillStyle = this.shape.fillColor;
            context.beginPath();
            context.moveTo(startX, upperStartY);
            context.lineTo(endX, upperEndY);
            context.lineTo(endX, lowerEndY);
            context.lineTo(startX, lowerStartY);
            context.closePath();
            context.fill();
            context.restore();
        });
    }
}

// Priority order for marker colors when merging (higher index = higher priority)
const MARKER_COLOR_PRIORITY: Record<string, number> = {
    '#595F63': 1, // Candlestick patterns (gray) - lowest
    '#7DD3FC': 2, // Chart patterns (sky)
    '#00BCD4': 2, // Supply zones (cyan)
    '#E91E63': 2, // Demand zones (magenta)
    '#FFEB00': 3, // Highs/Lows (yellow)
    yellow: 3, // Highs/Lows alternate
    '#26a69a': 4, // Long entries (green) - highest
    '#ef5350': 4 // Short entries (red) - highest
};

class ChartHelper {
    readonly container: HTMLElement;
    private readonly subCharts: Map<string, IChartApi> = new Map();
    private readonly chartContainers: Map<string, HTMLElement> = new Map();
    private readonly subChartSeries: Map<string, ISeriesApi<'Line' | 'Candlestick'>> = new Map();
    private chart?: IChartApi;
    private volumeSeries?: ISeriesApi<'Histogram'>;
    private bars: BarOHLCVDetailed[] = [];
    private barsTimestampMap: Map<UnixTimestamp, number> = new Map();
    private readonly lineSeriesMap: Map<string, ISeriesApi<'Line'>> = new Map();
    private readonly trendlineSeries: Map<string, ISeriesApi<'Line'>> = new Map();
    private readonly microTrendSeries: Map<string, ISeriesApi<'Line'>> = new Map();
    private readonly supplyDemandZoneSeries: Map<string, ISeriesApi<'Line'>> = new Map();
    private readonly chartPatternSeries: Map<string, ISeriesApi<'Line'>> = new Map();
    private readonly chartPatternPrimitives: Map<string, ISeriesPrimitive<Time>> = new Map();
    private readonly colorsForIndicators: Map<string, HexColor> = new Map();
    private keyLevels: KeyLevelItem[] | undefined;
    private candlestickSeries?: ISeriesApi<'Candlestick'>;
    private chartSettings: ChartSettings;
    private comparisonData: inferProcedureOutput<AppRouter['technicalAnalysis']['compareEntities']>['data'] | null = null;
    private readonly chartWidth: number = 1650;
    private readonly chartHeight: number = 700;
    private chartPatternSummary: ChartPatternSummary | null = null;
    private chartPatternSnapshots: ChartPatternResolvedSnapshot[] = [];
    private chartPatternSnapshotSeries: ChartPatternResolvedSnapshot[] = [];
    private chartPatternHistoricalOccurrences: ChartPatternResolvedOccurrence[] = [];
    private chartPatternHistoricalEvents: ChartPatternResolvedEvent[] = [];
    private chartPatternPitReplaySnapshots: ChartPatternResolvedSnapshot[] = [];
    private chartPatternPitReplayEvents: ChartPatternResolvedEvent[] = [];
    private shouldRenderChartPatternEventMarkers = true;
    // Track event subscriptions for cleanup
    private readonly eventSubscriptions: Map<IChartApi, (() => void)[]> = new Map();
    private selectedAnchoredPeriods: number[] = [];
    // Centralized marker storage by source for consolidated rendering
    private readonly markersBySource: Map<MarkerSource, SeriesMarker<Time>[]> = new Map();
    private resizeObserver: ResizeObserver | null = null;
    private resizeFrameId: number | null = null;
    private readonly handleWindowResize = () => {
        this.scheduleChartResize(true);
    };

    constructor(container: HTMLElement, chartSettings: ChartSettings) {
        this.container = container;
        this.chartSettings = chartSettings;
        this.registerChartContainer('main', container);
        this.observeChartLayout();
    }

    private getResolvedChartWidth(): number {
        return this.getResolvedChartWidthForContainer(this.container);
    }

    private getResolvedChartHeight(): number {
        return this.getResolvedChartHeightForContainer(this.container, this.chartHeight);
    }

    private getResolvedChartWidthForContainer(container: HTMLElement, fallbackWidth = this.chartWidth): number {
        const containerWidth = Math.round(container.clientWidth || container.getBoundingClientRect().width);
        return Math.max(1, containerWidth > 0 ? containerWidth : fallbackWidth);
    }

    private getResolvedChartHeightForContainer(container: HTMLElement, fallbackHeight: number): number {
        const containerHeight = Math.round(container.clientHeight || container.getBoundingClientRect().height);
        return Math.max(1, containerHeight > 0 ? containerHeight : fallbackHeight);
    }

    private registerChartContainer(chartId: string, container: HTMLElement): void {
        this.chartContainers.set(chartId, container);
    }

    private unregisterChartContainer(chartId: string): void {
        this.chartContainers.delete(chartId);
    }

    private observeChartLayout(): void {
        if (typeof ResizeObserver === 'function') {
            this.resizeObserver = new ResizeObserver(() => {
                this.scheduleChartResize(true);
            });

            this.resizeObserver.observe(this.container);
            return;
        }

        window.addEventListener('resize', this.handleWindowResize);
    }

    private resizeMainChart(forceRepaint = false): void {
        if (!this.chart) return;

        this.chart.resize(this.getResolvedChartWidth(), this.getResolvedChartHeight(), forceRepaint);
    }

    private resizeChart(chartId: string, forceRepaint = false): void {
        const chart = this.subCharts.get(chartId);
        const container = this.chartContainers.get(chartId);

        if (!chart || !container || this.isChartDisposed(chart)) {
            return;
        }

        chart.resize(
            this.getResolvedChartWidthForContainer(container),
            this.getResolvedChartHeightForContainer(container, chartId === 'main' ? this.chartHeight : 200),
            forceRepaint
        );
    }

    private resizeAllCharts(forceRepaint = false): void {
        for (const [chartId, chart] of this.subCharts) {
            if (this.isChartDisposed(chart)) {
                this.subCharts.delete(chartId);
                this.subChartSeries.delete(chartId);
                this.unregisterChartContainer(chartId);
                continue;
            }

            if (chartId === 'main') {
                this.resizeMainChart(forceRepaint);
            } else {
                this.resizeChart(chartId, forceRepaint);
            }
        }
    }

    private scheduleChartResize(forceRepaint = false): void {
        if (this.resizeFrameId != null) {
            window.cancelAnimationFrame(this.resizeFrameId);
        }

        this.resizeFrameId = window.requestAnimationFrame(() => {
            this.resizeFrameId = null;
            this.resizeAllCharts(forceRepaint);
        });
    }

    private async settleMainChartLayout(forceRepaint = false): Promise<void> {
        await new Promise<void>((resolve) => {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    this.resizeMainChart(forceRepaint);
                    resolve();
                });
            });
        });
    }

    // Helper method to register event subscriptions for cleanup
    private registerEventSubscription(chart: IChartApi, unsubscribeFn: () => void): void {
        if (!this.eventSubscriptions.has(chart)) {
            this.eventSubscriptions.set(chart, []);
        }

        this.eventSubscriptions.get(chart)?.push(unsubscribeFn);
    }

    // Helper method to unsubscribe all events for a chart
    private unsubscribeChartEvents(chart: IChartApi): void {
        const subscriptions = this.eventSubscriptions.get(chart) || [];

        // Call each unsubscribe function
        subscriptions.forEach((unsubscribe) => {
            try {
                unsubscribe();
            } catch (error) {
                console.debug('Error while unsubscribing chart event:', error);
            }
        });

        // Clear subscriptions for this chart
        this.eventSubscriptions.delete(chart);
    }

    /**
     * Register markers from a specific source. Markers are stored and then
     * consolidated with markers from other sources before rendering.
     */
    private registerMarkers(source: MarkerSource, markers: SeriesMarker<Time>[]): void {
        this.markersBySource.set(source, markers);
        this.renderConsolidatedMarkers();
    }

    /**
     * Clear markers from a specific source and re-render.
     */
    private clearMarkerSource(source: MarkerSource): void {
        this.markersBySource.delete(source);
        this.renderConsolidatedMarkers();
    }

    /**
     * Consolidate markers from all sources, merge overlapping markers,
     * and render them on the chart.
     */
    private renderConsolidatedMarkers(): void {
        if (!this.candlestickSeries) return;

        // Collect all markers from all sources
        const allMarkers: SeriesMarker<Time>[] = [];

        for (const markers of this.markersBySource.values()) {
            allMarkers.push(...markers);
        }

        if (allMarkers.length === 0) {
            createSeriesMarkers(this.candlestickSeries, []);
            return;
        }

        // Group markers by time and position to find overlaps
        const grouped = this.groupMarkersByTimeAndPosition(allMarkers);

        // Merge overlapping markers
        const consolidatedMarkers = this.mergeOverlappingMarkers(grouped);

        // Sort by time (required by lightweight-charts)
        consolidatedMarkers.sort((a, b) => {
            const timeA = typeof a.time === 'object' ? new Date(a.time.year, a.time.month - 1, a.time.day).getTime() : Number(a.time);
            const timeB = typeof b.time === 'object' ? new Date(b.time.year, b.time.month - 1, b.time.day).getTime() : Number(b.time);
            return timeA - timeB;
        });

        createSeriesMarkers(this.candlestickSeries, consolidatedMarkers);
    }

    /**
     * Group markers by their time and position to identify overlaps.
     */
    private groupMarkersByTimeAndPosition(markers: SeriesMarker<Time>[]): Map<string, SeriesMarker<Time>[]> {
        const grouped = new Map<string, SeriesMarker<Time>[]>();

        for (const marker of markers) {
            // Create a key from time and position
            const timeKey =
                typeof marker.time === 'object' ? `${marker.time.year}-${marker.time.month}-${marker.time.day}` : String(marker.time);

            const key = `${timeKey}|${marker.position}`;

            if (!grouped.has(key)) {
                grouped.set(key, []);
            }

            grouped.get(key)!.push(marker);
        }

        return grouped;
    }

    /**
     * Merge overlapping markers by combining their text and using
     * the highest priority color.
     */
    private mergeOverlappingMarkers(grouped: Map<string, SeriesMarker<Time>[]>): SeriesMarker<Time>[] {
        const result: SeriesMarker<Time>[] = [];

        for (const markers of grouped.values()) {
            if (markers.length === 1) {
                result.push(markers[0]);
            } else {
                // Sort by color priority (highest first) so we use the most important color
                markers.sort((a, b) => {
                    const priorityA = MARKER_COLOR_PRIORITY[a.color] ?? 0;
                    const priorityB = MARKER_COLOR_PRIORITY[b.color] ?? 0;
                    return priorityB - priorityA;
                });

                // Combine text from all markers, removing duplicates
                const uniqueTexts = [...new Set(markers.map((m) => m.text).filter(Boolean))];
                const combinedText = uniqueTexts.join(' | ');

                // Use properties from highest priority marker
                result.push({
                    ...markers[0],
                    text: combinedText
                });
            }
        }

        return result;
    }

    // Create the chart
    createChart() {
        // Check if container has dimensions
        if (this.container.clientHeight === 0) {
            console.warn('Chart container has zero height. Setting minimum height to ensure visibility.');
            this.container.style.height = '750px';
        }

        // Clear any previous chart instances
        if (this.chart) {
            console.log('Disposing previous chart instance');
            this.chart.remove();
            this.chart = undefined;
        }

        // Clear the container to prevent duplicate charts
        this.container.innerHTML = '';

        // Create the chart
        try {
            this.chart = createChart(this.container, {
                layout: {
                    background: { color: '#222' },
                    textColor: '#DDD'
                },
                grid: {
                    vertLines: { color: '#444' },
                    horzLines: { color: '#444' }
                },
                timeScale: {
                    timeVisible: true,
                    secondsVisible: false,
                    borderColor: '#555'
                },
                crosshair: {
                    mode: CrosshairMode.Normal
                },
                localization: {
                    locale: 'en-US'
                },
                width: this.getResolvedChartWidth(),
                height: this.getResolvedChartHeight()
            });

            // Keep a reference to the "main" chart in our subCharts map
            this.subCharts.set('main', this.chart);

            this.settleMainChartLayout(true).catch((error) => {
                console.error('Error settling main chart layout:', error);
            });

            return this.chart;
        } catch (error) {
            console.error('Error creating chart:', error);
            return undefined;
        }
    }

    // Add volume series to the chart
    addVolumeSeries() {
        if (!this.chart) return;

        this.volumeSeries = this.chart.addSeries(HistogramSeries, {
            priceFormat: {
                type: 'volume'
            },
            priceScaleId: ''
        });

        this.volumeSeries.priceScale().applyOptions({
            scaleMargins: {
                top: 0.7,
                bottom: 0
            }
        });

        this.volumeSeries.setData(createTradingViewVolumeFromBars(this.bars));
    }

    // Add candlestick series to the chart
    addCandleStickSeries() {
        if (!this.chart) return;

        this.candlestickSeries = this.chart.addSeries(CandlestickSeries);

        this.candlestickSeries.setData(convertBarsToTradingView(this.bars));

        this.subChartSeries.set('main', this.candlestickSeries);
        this.chart.timeScale().fitContent();
    }

    // Build the chart with bars and volume
    buildChart() {
        const { shouldIncludeCandlestickPatterns, shouldIncludeChartPatterns, markers, shouldIncludeVolumeSeries } = this.chartSettings;

        this.createChart();

        if (this.bars?.length === 0) {
            console.warn('No bars data available for chart rendering');
            return;
        }

        this.addCandleStickSeries();

        if (shouldIncludeVolumeSeries) {
            this.addVolumeSeries();
        }

        if (shouldIncludeCandlestickPatterns) {
            this.addCandlestickPatterns();
        }

        if (shouldIncludeChartPatterns) {
            this.addChartPatterns();
        }

        if (markers?.length) {
            this.addMarkers(markers);
        }

        // Make sure we fit the content after adding series
        if (this.chart) {
            this.chart.timeScale().fitContent();
        }

        console.log('Chart built successfully with candlestick and volume series');
    }

    updateSettings(settings: ChartSettings) {
        this.chartSettings = settings;
    }

    setChartPatternData(config: {
        snapshots: ChartPatternResolvedSnapshot[];
        snapshotSeries: ChartPatternResolvedSnapshot[];
        historicalOccurrences: ChartPatternResolvedOccurrence[];
        historicalEvents: ChartPatternResolvedEvent[];
        pitReplaySnapshots: ChartPatternResolvedSnapshot[];
        pitReplayEvents: ChartPatternResolvedEvent[];
        summary: ChartPatternSummary | null;
        shouldRenderEventMarkers: boolean;
    }) {
        this.chartPatternSnapshots = config.snapshots;
        this.chartPatternSnapshotSeries = config.snapshotSeries;
        this.chartPatternHistoricalOccurrences = config.historicalOccurrences;
        this.chartPatternHistoricalEvents = config.historicalEvents;
        this.chartPatternPitReplaySnapshots = config.pitReplaySnapshots;
        this.chartPatternPitReplayEvents = config.pitReplayEvents;
        this.chartPatternSummary = config.summary;
        this.shouldRenderChartPatternEventMarkers = config.shouldRenderEventMarkers;
    }

    // Update chart with new bar data
    async updateBars(
        newBars: BarOHLCVDetailed[],
        comparisonData: inferProcedureOutput<AppRouter['technicalAnalysis']['compareEntities']>['data']
    ): Promise<void> {
        const {
            shouldIncludeCandlestickPatterns,
            shouldIncludeChartPatterns,
            shouldIncludeTrendlines,
            shouldIncludeHighsAndLows,
            shouldIncludeMicroTrends,
            shouldIncludeSupplyDemandZones,
            markers,
            tooltipMarkers,
            consoleItems,
            chartLines,
            priceLines,
            priceTrajectories,
            legend,
            shouldIncludeVolumeSeries
        } = this.chartSettings;

        if (newBars?.length === 0) {
            console.warn('No bars data provided to updateBars method');
            this.resetChart();
            return;
        }

        // Update internal bars array
        this.bars = newBars;
        this.barsTimestampMap = new Map(newBars.map((bar, index) => [bar.t, index]));
        this.comparisonData = comparisonData;

        if (!this.chart) {
            console.warn('Chart not yet initialized, creating it first...');
            this.createChart();
        }

        // Completely clear and recreate the series for a fresh update
        this.resetChart();

        // Add series back with new data
        this.addCandleStickSeries();

        if (shouldIncludeVolumeSeries) {
            this.addVolumeSeries();
        }

        this.buildLineSeriesIndicators();

        if (shouldIncludeCandlestickPatterns) {
            this.addCandlestickPatterns();
        }

        if (shouldIncludeChartPatterns) {
            this.addChartPatterns();
        }

        if (shouldIncludeTrendlines) {
            await this.addTrendlines();
        }

        if (shouldIncludeHighsAndLows) {
            await this.addHighsAndLows();
        }

        if (shouldIncludeMicroTrends) {
            await this.addMicroTrends();
        }

        if (shouldIncludeSupplyDemandZones) {
            await this.addSupplyDemandZones();
        }

        if (markers?.length) {
            this.addMarkers(markers);
        }

        if (tooltipMarkers?.length) {
            this.addChartTooltipMarkers(tooltipMarkers);
        }

        if (consoleItems?.length) {
            this.addChartConsoleItems(consoleItems);
        }

        if (chartLines?.length) {
            chartLines.forEach((line) => this.addChartLine(line));
        }

        if (priceLines?.length) {
            priceLines.forEach((line) => this.addPriceLine(line));
        }

        if (priceTrajectories?.length) {
            priceTrajectories.forEach((trajectory) => this.addPriceTrajectory(trajectory));
        }

        if (legend) {
            this.addLegend(legend, this.container);
        }

        // Check if we have comparison data and create comparison charts
        if (this.comparisonData) {
            this.createComparisonCharts();
        }

        // Fit content to view
        if (this.chart) {
            this.chart.timeScale().applyOptions({
                rightOffset: MAIN_CHART_RIGHT_OFFSET_BARS
            });

            this.chart.timeScale().fitContent();
        }

        await this.settleMainChartLayout(true);

        if (this.chart) {
            this.chart.timeScale().fitContent();
        }

        this.applyFocusedChartPatternVisibleRange();

        // Sync crosshairs after everything is built
        this.syncAllCharts();
    }

    buildLineSeriesForIndicator(indicatorObject: IndicatorInputWithChartSettings, property: string = '', colorKey = 0) {
        if (!this.chart) return;
        const { bars } = this;
        const { colors, indicator } = indicatorObject;

        const lineSeries = this.chart.addSeries(LineSeries, {
            color: colors[colorKey],
            lineWidth: LINE_WIDTH_PIXELS
        });

        this.colorsForIndicators.set(indicatorObject.id, colors[colorKey] as HexColor);

        const indicatorData = convertDurationIndicatorBarsToTradingView({
            bars,
            indicator,
            property
        });

        lineSeries.setData(indicatorData);

        this.lineSeriesMap.set(indicatorObject.id + property, lineSeries);
    }

    buildLineSeriesIndicators() {
        if (!this.chart) return;

        const { bars } = this;

        for (const indicatorObject of this.chartSettings.indicatorsWithChartSettings) {
            const { indicator, colors } = indicatorObject;

            // Overlay indicators with multiple lines
            if (indicator.type === 'donchian') {
                // Donchian has upper, lower, middle
                let i = 0;

                for (const property of ['upper', 'lower', 'middle']) {
                    this.buildLineSeriesForIndicator(indicatorObject, property, i);
                    i++;
                }
            } else if (indicator.type === 'bollinger') {
                // Bollinger also typically has upper, middle, lower
                let i = 0;

                for (const property of ['upper', 'middle', 'lower']) {
                    this.buildLineSeriesForIndicator(indicatorObject, property, i);
                    i++;
                }
            } else if (indicator.type === 'ichimoku') {
                // Ichimoku typically has 5 lines: tenkan, kijun, senkouA, senkouB, chikou
                // The data converter must return correct data for each "property" suffix
                let i = 0;

                for (const property of ['tenkan', 'kijun', 'senkouA', 'senkouB', 'chikou']) {
                    this.buildLineSeriesForIndicator(indicatorObject, property, i);
                    i++;
                }
            } else if (indicator.type === 'vwap') {
                // VWAP now has multiple lines: vwap (main), upper1, lower1, upper2, lower2
                let i = 0;

                for (const property of ['vwap', 'upper1', 'lower1', 'upper2', 'lower2']) {
                    this.buildLineSeriesForIndicator(indicatorObject, property, i);
                    i++;
                }
            }
            // Indicators that use subcharts
            else if (
                indicator.type === 'rsi' ||
                indicator.type === 'macd' ||
                indicator.type === 'stochasticrsi' ||
                indicator.type === 'atr' ||
                indicator.type === 'stochastic' ||
                indicator.type === 'adx' ||
                indicator.type === 'lwti' ||
                indicator.type === 'vma'
            ) {
                // Find the subChartContainer using React-friendly approach
                const subChartContainer = document.getElementById('subChartContainer');

                if (!subChartContainer) {
                    console.error('Sub chart container not found');
                    continue;
                }

                const indicatorStringId = `${indicatorObject.id}`;
                const chartContainerId = `${indicatorStringId}ChartContainer`;

                // Check if this sub-chart is already created
                const existingContainer = document.getElementById(chartContainerId);
                if (existingContainer) continue;

                // Create a new div element for the subchart in a React-friendly way
                const newChartContainer = document.createElement('div');
                newChartContainer.setAttribute('id', chartContainerId);
                newChartContainer.style.position = 'relative';
                newChartContainer.style.width = '100%';
                newChartContainer.style.height = '200px';
                newChartContainer.style.marginBottom = '8px';

                // Append to the subChartContainer
                subChartContainer.appendChild(newChartContainer);

                // Create the subchart
                const newSubChart = createChart(newChartContainer, {
                    height: 200,
                    layout: {
                        background: { color: '#222' },
                        textColor: '#DDD'
                    },
                    grid: {
                        vertLines: { color: '#444' },
                        horzLines: { color: '#444' }
                    },
                    timeScale: {
                        timeVisible: true
                    },
                    width: this.getResolvedChartWidthForContainer(newChartContainer)
                });

                let neededProperties: string[];

                if (indicator.type === 'macd') {
                    neededProperties = ['macd', 'signal', 'histogram'];
                } else if (indicator.type === 'stochasticrsi') {
                    neededProperties = ['k', 'd'];
                } else if (indicator.type === 'stochastic') {
                    neededProperties = ['k', 'd'];
                } else if (indicator.type === 'adx') {
                    neededProperties = ['adx', 'plusDi', 'minusDi'];
                } else {
                    // rsi, atr => single line property
                    neededProperties = [undefined as unknown as string];
                }

                const colorsUsed: HtmlColor[] = [];

                for (let i = 0; i < neededProperties.length; i++) {
                    const property = neededProperties[i];
                    const color = colors[i];

                    const newSeries = newSubChart.addSeries(LineSeries, {
                        color,
                        lineWidth: 2
                    });

                    const indicatorData = convertDurationIndicatorBarsToTradingView({ bars, indicator, property });
                    newSeries.setData(indicatorData);

                    this.subChartSeries.set(chartContainerId, newSeries);
                    colorsUsed.push(color);
                }

                // Add sub-chart legend
                if (indicator.type === 'rsi' || indicator.type === 'atr') {
                    // Single-line sub-chart
                    this.addLegend(`${indicator.type.toUpperCase()} ${indicator.period}`, newChartContainer);
                } else if (indicator.type === 'macd') {
                    // MACD
                    this.addLegend(
                        `${indicator.type} <span style="color:${colorsUsed[0] ?? ''}">Fast ${indicator.fastPeriod}</span>` +
                            ` <span style="color:${colorsUsed[1] ?? ''}">Slow ${indicator.slowPeriod}</span>` +
                            ` <span style="color:${colorsUsed[2] ?? ''}">Signal ${indicator.signalPeriod}</span>`,
                        newChartContainer,
                        '14px'
                    );
                } else if (indicator.type === 'stochasticrsi') {
                    this.addLegend(
                        `${indicator.type} <span style="color:${colorsUsed[0] ?? ''}">k ${indicator.kPeriod}</span>` +
                            ` <span style="color:${colorsUsed[1] ?? ''}">d ${indicator.dPeriod}</span>` +
                            ` <span style="color:white">RSI ${indicator.rsiPeriod}, Stoch ${indicator.stochasticPeriod}</span>`,
                        newChartContainer,
                        '14px'
                    );
                } else if (indicator.type === 'stochastic') {
                    this.addLegend(
                        `${indicator.type} <span style="color:${colorsUsed[0] ?? ''}">K ${indicator.kPeriod}</span>` +
                            ` <span style="color:${colorsUsed[1] ?? ''}">D ${indicator.dPeriod}</span>` +
                            ` <span style="color:white">Lookback ${indicator.lookback}</span>`,
                        newChartContainer,
                        '14px'
                    );
                }

                // Store the new chart for reference
                this.subCharts.set(chartContainerId, newSubChart);
                this.registerChartContainer(chartContainerId, newChartContainer);
                this.scheduleChartResize(true);
            } else {
                // Single-line overlay (e.g. sma, ema, vwma, or any single-line that isn't sub-chart)
                // or single-line multi period.
                this.buildLineSeriesForIndicator(indicatorObject);
            }
        }
    }

    private isChartDisposed(chart: IChartApi): boolean {
        try {
            // If a chart is disposed, accessing any method will throw an error
            // This is a safe way to check if a chart is still valid
            chart.timeScale();
            return false;
        } catch {
            // If an error is thrown, the chart is likely disposed
            return true;
        }
    }

    private getValidCharts(): IChartApi[] {
        const validCharts: IChartApi[] = [];

        // Iterate through all charts and check if they're valid
        for (const [chartId, chart] of this.subCharts) {
            if (this.isChartDisposed(chart)) {
                // Chart is disposed, remove it from maps
                this.subCharts.delete(chartId);
                this.subChartSeries.delete(chartId);
                console.debug(`Removed disposed chart: ${chartId}`);
            } else {
                validCharts.push(chart);
            }
        }

        return validCharts;
    }

    syncAllCharts(): void {
        // Get only valid, non-disposed charts
        const allCharts = this.getValidCharts();

        if (allCharts.length < 2) {
            return; // only one chart or no charts, nothing to sync
        }

        const mainChartId = 'main';
        const mainChart = this.subCharts.get(mainChartId);

        // Check if main chart is valid
        if (mainChart && this.isChartDisposed(mainChart)) {
            this.subCharts.delete(mainChartId);
            this.subChartSeries.delete(mainChartId);
            return; // Main chart is disposed, can't sync
        }

        const mainSeries = this.candlestickSeries;

        // Clean up existing subscriptions first
        allCharts.forEach((chart) => {
            this.unsubscribeChartEvents(chart);
        });

        // For each chart, subscribe to crosshair events
        allCharts.forEach((chartA) => {
            // Safe subscribeCrosshairMove with cleanup
            try {
                const unsubscribeCrosshair = chartA.subscribeCrosshairMove((param) => {
                    try {
                        // If the mouse is off chartA or there's no valid time
                        if (!param.point || param.time === undefined) {
                            // Clear crosshair in all other charts
                            this.getValidCharts().forEach((chartB) => {
                                if (chartB !== chartA && !this.isChartDisposed(chartB)) {
                                    chartB.clearCrosshairPosition();
                                }
                            });

                            return;
                        }

                        const y = param.point.y as number;
                        let price = 0;

                        // If we are on the main chart
                        if (mainSeries && chartA === mainChart) {
                            price = mainSeries.coordinateToPrice(y) ?? 0;
                        }

                        // Now set the crosshair on every other chart
                        for (const [chartId, chart] of this.subCharts) {
                            if (chart === chartA) continue;

                            // Check if chart is still valid
                            if (this.isChartDisposed(chart)) {
                                this.subCharts.delete(chartId);
                                this.subChartSeries.delete(chartId);
                                continue;
                            }

                            const series = this.subChartSeries.get(chartId);
                            if (!series) continue;

                            try {
                                chart.setCrosshairPosition(price, param.time, series);
                            } catch {
                                console.debug(`Failed to set crosshair on chart ${chartId}`);
                            }
                        }
                    } catch {
                        console.debug('Error in crosshair move handler');
                    }
                });

                // Register for cleanup
                if (typeof unsubscribeCrosshair === 'function') {
                    this.registerEventSubscription(chartA, unsubscribeCrosshair);
                }

                // Only sync zoom for the main chart
                // Sub-charts should not affect the main chart's zoom
                if (chartA === this.chart) {
                    try {
                        const unsubscribeTimeScale = chartA.timeScale().subscribeVisibleTimeRangeChange((newRange) => {
                            try {
                                if (!newRange) return;

                                // Only sync from main chart to sub-charts, not vice versa
                                this.getValidCharts().forEach((chartB) => {
                                    if (chartB !== chartA && chartB !== this.chart) {
                                        try {
                                            chartB.timeScale().setVisibleRange(newRange);
                                        } catch {
                                            console.debug('Error setting visible range');
                                        }
                                    }
                                });
                            } catch {
                                console.debug('Error in time range change handler');
                            }
                        });

                        // Register for cleanup
                        if (typeof unsubscribeTimeScale === 'function') {
                            this.registerEventSubscription(chartA, unsubscribeTimeScale);
                        }
                    } catch {
                        console.debug('Error subscribing to time scale');
                    }
                }
            } catch {
                console.debug('Error subscribing to crosshair');
            }
        });
    }

    addLegend(text: string, container: HTMLElement, fontSize = '14px') {
        // Ensure the container has position: relative to properly contain absolute positioned elements
        if (window.getComputedStyle(container).position === 'static') {
            container.style.position = 'relative';
        }

        const legend = document.createElement('div');

        legend.setAttribute(
            'style',
            `position: absolute; left: 12px; top: 12px; z-index: 5; color: #6a6a6a;
            font-size: ${fontSize}; font-family: sans-serif; line-height: 18px; font-weight: 300;`
        );

        legend.innerHTML = `
            <div style='font-size: ${fontSize}; margin: 4px 0px;'>
                ${text}
            </div>
        `;

        container.appendChild(legend);
    }

    removeAllLineSeries() {
        for (const series of this.lineSeriesMap.values()) {
            this.chart?.removeSeries(series);
        }

        this.lineSeriesMap.clear();
    }

    removeAllSubCharts() {
        // Clean up event subscriptions and remove charts
        for (const [chartId, chart] of this.subCharts) {
            if (chartId === 'main') continue; // Skip the main chart

            try {
                // Unsubscribe any events
                this.unsubscribeChartEvents(chart);

                // Remove chart from DOM and memory
                if (!this.isChartDisposed(chart)) {
                    chart.remove();
                }
            } catch (error) {
                console.debug(`Error removing subchart ${chartId}:`, error);
            }

            // Remove from maps
            this.subCharts.delete(chartId);
            this.subChartSeries.delete(chartId);
            this.unregisterChartContainer(chartId);

            // Remove container if it exists
            const subChartContainer = document.getElementById(chartId);
            if (subChartContainer) subChartContainer.remove();
        }

        // Keep the main chart, remove everything else
        const mainChart = this.subCharts.get('main');
        this.subCharts.clear();

        if (mainChart) {
            this.subCharts.set('main', mainChart);
        }

        // Clear series map except for main series
        const mainSeries = this.subChartSeries.get('main');
        this.subChartSeries.clear();

        if (mainSeries) {
            this.subChartSeries.set('main', mainSeries);
        }
    }

    // Reset chart series - useful for duration changes where we want a complete reset
    resetChart() {
        if (!this.chart) {
            console.warn('Chart not yet initialized, creating it first...');
            this.createChart();
            return;
        }

        try {
            // Unsubscribe all chart events
            if (this.chart) {
                this.unsubscribeChartEvents(this.chart);
            }

            // Check and clean up all subcharts
            for (const chart of this.subCharts.values()) {
                if (chart !== this.chart) {
                    this.unsubscribeChartEvents(chart);
                }
            }

            // Completely clear and recreate the series for a fresh update
            if (this.candlestickSeries) {
                this.chart.removeSeries(this.candlestickSeries);
                this.candlestickSeries = undefined;
            }

            if (this.volumeSeries) {
                this.chart.removeSeries(this.volumeSeries);
                this.volumeSeries = undefined;
            }

            // Reset any indicators
            this.removeAllLineSeries();
            this.removeAllSubCharts();
            this.removeTrendlines();
            this.removeMicroTrends();
            this.removeSupplyDemandZones();
            this.removeChartPatterns();

            // Clear all markers from consolidated storage
            this.markersBySource.clear();

            // Delete the comparison chart container
            const comparisonChartContainer = document.getElementById('comparison-charts-container');
            if (comparisonChartContainer) comparisonChartContainer.remove();
        } catch (error) {
            console.error('Error during chart reset:', error);

            // Recreate the chart as a fallback
            if (this.chart) {
                this.chart.remove();

                this.chart = undefined;
            }

            this.createChart();
        }
    }

    addLevelLine(details: { keyLevel: KeyLevelItem; bars: BarOHLCVDetailed[]; indicatorColor: string }) {
        if (!this.chart) return;

        const { bars, indicatorColor, keyLevel } = details;
        if (!bars[0]) return;

        const lineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 1,
            color: indicatorColor
        });

        const levelLineData = createLevelLineDataFromBars(bars, keyLevel.value);
        lineSeries.setData(levelLineData);

        const levelSubType = Object.keys(KEY_LEVEL_SUBTYPES).find((key) => KEY_LEVEL_SUBTYPES[key] === keyLevel.levelSubType);

        if (!levelSubType) throw new Error('Invalid level subtype');

        const markerData: SeriesMarker<Time> = {
            time: this.chartSettings.duration.endsWith('m')
                ? convertEstTimestampToTradingView(getUnixTime(bars[0].t))
                : getDate({ date: bars[0].t }),
            position: 'aboveBar',
            shape: 'arrowDown',
            size: 0,
            color: indicatorColor,
            text: levelSubType
        };

        createSeriesMarkers(lineSeries, [markerData]);

        this.chart.timeScale().fitContent();
        this.lineSeriesMap.set(`KeyLevel-${keyLevel.id}`, lineSeries);
    }

    addMarker(markerDateTime: string, position: 'aboveBar' | 'belowBar' | 'inBar', markerText?: string | null) {
        // TODO - anyway to get these? We used to be able to get them from the series
        const markers: SeriesMarker<Time>[] = [];

        markers.push({
            time: this.chartSettings.duration.endsWith('m')
                ? convertEstTimestampToTradingView(getUnixTime(markerDateTime))
                : getDate({ date: markerDateTime }),
            position,
            shape: 'arrowDown',
            color: '#FFEB00',
            text: markerText || 'Marker'
        });

        if (this.candlestickSeries) createSeriesMarkers(this.candlestickSeries, markers);
    }

    addPriceTrajectory(trajectory: PriceTrajectoryCondensed) {
        if (!this.chart) return;

        const lineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 1,
            color: '#FFEB00'
        });

        const data: SingleValueData[] = [];

        if (trajectory.startIndex === -1 || trajectory.endIndex === -1) return;

        let currentPrice = trajectory.startingPoint === 'top' ? this.bars[trajectory.startIndex].h : this.bars[trajectory.startIndex].l;

        for (let i = trajectory.startIndex; i <= trajectory.endIndex; i++) {
            const bar = this.bars[i];

            currentPrice += trajectory.priceChangePerBar;

            data.push({ time: convertEstTimestampToTradingView(bar.t), value: currentPrice });
        }

        lineSeries.setData(data);
    }

    addChartLine(line: ChartLine) {
        if (!this.chart) return;

        const lineStartingBarIndex = this.barsTimestampMap.get(line.startTimestamp);
        const lineEndingBarIndex = this.barsTimestampMap.get(line.endTimestamp);

        if (lineStartingBarIndex === undefined || lineEndingBarIndex === undefined) return;

        const lineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 1,
            color: line.color
        });

        // For the same bar, we have to draw a line straight up/down
        if (lineStartingBarIndex === lineEndingBarIndex) {
            const bar = this.bars[lineStartingBarIndex];
            const verticalLineLeadTimestamp = convertEstTimestampToTradingView(bar.t - 30);
            const verticalLineLagTimestamp = convertEstTimestampToTradingView(bar.t + 30);

            const data: SingleValueData[] =
                line.startPosition === 'bottom'
                    ? [
                          { time: verticalLineLeadTimestamp, value: bar.l },
                          { time: verticalLineLagTimestamp, value: bar.h }
                      ]
                    : [
                          { time: verticalLineLeadTimestamp, value: bar.h },
                          { time: verticalLineLagTimestamp, value: bar.l }
                      ];

            lineSeries.setData(data);
            return;
        }

        const bars = this.bars.slice(lineStartingBarIndex, lineEndingBarIndex + 1);
        const data: SingleValueData[] = [];

        if (!bars.length) return;
        let lastValue = 0;

        const startingPrice = line.startPosition === 'bottom' ? bars[0].l : bars[0].h;
        const endingPrice = line.endPosition === 'bottom' ? bars[bars.length - 1].l : bars[bars.length - 1].h;

        const priceChange = endingPrice - startingPrice;
        const priceChangePerBar = priceChange / (bars.length - 1);

        for (let i = 0; i < bars.length; i++) {
            const bar = bars[i];

            const value = i === 0 ? startingPrice : lastValue + priceChangePerBar;

            lastValue = value;

            data.push({
                time: convertEstTimestampToTradingView(bar.t),
                value
            });
        }

        lineSeries.setData(data);
    }

    addMarkers(markers: ChartMarker[]) {
        const seriesMarkers: SeriesMarker<Time>[] = [];
        const firstBar = this.bars[0];
        const lastBar = this.bars.at(-1);
        const { duration, endDateTime } = this.chartSettings;
        // Bars are stamped at their start. Include events inside the last candle,
        // but never let lightweight-charts pin an outside event to an edge bar.
        const coverageEnd = lastBar
            ? duration.endsWith('m')
                ? lastBar.t + Number.parseInt(duration, 10) * 60
                : getUnixTimePlusMinus('+', 1, duration === '1mo' ? 'month' : duration === '1w' ? 'week' : 'day', undefined, lastBar.t)
            : null;
        const requestedEnd = getUnixTime(endDateTime);

        for (const marker of markers) {
            const markerTimestamp = getUnixTime(marker.dateTime);
            if (
                !firstBar ||
                coverageEnd === null ||
                !Number.isFinite(markerTimestamp) ||
                markerTimestamp < firstBar.t ||
                markerTimestamp >= coverageEnd ||
                markerTimestamp > requestedEnd
            ) {
                continue;
            }

            // Determine color based on marker text (Long Entry = green, Short Entry = red, default = yellow)
            let color: string = marker.color ?? 'yellow';
            const normalizedMarkerText = marker.text.trim().toLowerCase();

            if (marker.color === undefined && (normalizedMarkerText.includes('long') || /^l\d*$/.test(normalizedMarkerText))) {
                color = '#26a69a'; // Green
            } else if (marker.color === undefined && (normalizedMarkerText.includes('short') || /^s\d*$/.test(normalizedMarkerText))) {
                color = '#ef5350'; // Red
            }

            seriesMarkers.push({
                time: this.chartSettings.duration.endsWith('m')
                    ? convertEstTimestampToTradingView(markerTimestamp)
                    : getDate({ date: marker.dateTime }),
                position: marker.position,
                text: marker.text,
                shape: marker.shape ?? (marker.position === 'aboveBar' ? 'arrowDown' : 'arrowUp'),
                color,
                size: marker.size
            });
        }

        // Use consolidated marker system for strategy entries
        this.registerMarkers('strategy-entries', seriesMarkers);
    }

    async addTrendlines() {
        const { entity, startDateTime, endDateTime } = this.chartSettings;

        try {
            const trendlinesResponse = await trpc.entity.fetchTrendlines.query({
                entityId: entity.id,
                entityType: entity.entityType,
                startDateTime,
                endDateTime,
                duration: this.chartSettings.duration
            });

            if (!trendlinesResponse?.success) {
                console.error('Failed to fetch trendlines:', trendlinesResponse?.errorMessages);
                return;
            }

            // TODO - bring back tradeable filter? It was removing all
            // const tradeableTrendlines = trendlinesResponse.trendlines
            //     .filter((trendline) => trendline.endTimestamp && isTrendlineTradeable(trendline))
            //     .sort((a, b) => a.endTimestamp! - b.endTimestamp!);

            for (const tl of trendlinesResponse.trendlines) {
                this.addTrendlineLineSeries(tl);
            }
        } catch (error) {
            console.error('Error fetching trendlines:', error);
        }
    }

    addTrendlineLineSeries(trendline: Trendline) {
        if (!this.chart) return;

        const lineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 1
        });

        lineSeries.setData(createTrendlineDataFromBars(trendline));

        this.trendlineSeries.set(
            trendline.trendPercent.toString() + trendline.startDateTime.toString() + Math.floor(Math.random() * 99999).toString(),
            lineSeries
        );

        this.chart.timeScale().fitContent();
    }

    addMicroTrendLineSeries(microtrend: MicrotrendApiObject) {
        if (!this.chart || !microtrend.bars[0]) return;

        let color = '#f0ff00';
        color = microtrend.type === 'uptrend' ? '#00ff42' : color;
        color = microtrend.type === 'downtrend' ? '#ff00de' : color;

        const lineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 2,
            color
        });

        const startingMidpoint = microtrend.bars[0].s.cmp;
        const startingTimestamp = microtrend.bars[0].t;
        const midpointDiff = last(microtrend.bars)!.s.cmp - startingMidpoint;
        const changePerBar = midpointDiff / (microtrend.bars.length - 1);

        let lastValue = startingMidpoint;
        const midpointMedian = arrayMedian(microtrend.bars.map((b) => b.s.cmp));

        const data: SingleValueData[] = microtrend.bars.map((bar) => {
            const isDirectionalMicrotrend = microtrend.type === 'uptrend' || microtrend.type === 'downtrend';
            const value = isDirectionalMicrotrend
                ? startingTimestamp === bar.t
                    ? startingMidpoint
                    : lastValue + changePerBar
                : midpointMedian;

            if (isDirectionalMicrotrend) {
                lastValue = value;
            }

            return {
                time: convertEstTimestampToTradingView(bar.t),
                value
            };
        });

        lineSeries.setData(data);

        this.microTrendSeries.set(microtrend.startDateTime + Math.floor(Math.random() * 99999).toString(), lineSeries);
    }

    /**
     * Adds line series for a supply/demand zone and returns a marker for consolidation.
     * Returns null if no bars are available for the zone.
     */
    addSupplyDemandZoneLineSeries(supplyDemandZone: SupplyDemandZone): SeriesMarker<Time> | null {
        if (!this.chart) return null;

        const zoneRange = this.getSupplyDemandZoneRenderRange(supplyDemandZone.startTimestamp);
        if (!zoneRange) return null;

        const color = getRandomHtmlColor();

        const bottomLineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 2,
            color
        });

        const topLineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 2,
            color
        });

        const lowData: SingleValueData[] = [
            {
                time: convertEstTimestampToTradingView(zoneRange.startTimestamp),
                value: supplyDemandZone.priceLow
            },
            {
                time: convertEstTimestampToTradingView(zoneRange.endTimestamp),
                value: supplyDemandZone.priceLow
            }
        ];

        bottomLineSeries.setData(lowData);

        this.supplyDemandZoneSeries.set(supplyDemandZone.startDateTime + Math.floor(Math.random() * 99999).toString(), bottomLineSeries);

        const highData: SingleValueData[] = [
            {
                time: convertEstTimestampToTradingView(zoneRange.startTimestamp),
                value: supplyDemandZone.priceHigh
            },
            {
                time: convertEstTimestampToTradingView(zoneRange.endTimestamp),
                value: supplyDemandZone.priceHigh
            }
        ];

        topLineSeries.setData(highData);

        this.supplyDemandZoneSeries.set(supplyDemandZone.startDateTime + Math.floor(Math.random() * 99999).toString(), topLineSeries);

        // Return the marker for consolidation (don't render directly)
        return {
            time: convertEstTimestampToTradingView(zoneRange.startTimestamp),
            position: supplyDemandZone.type === 'supply' ? 'aboveBar' : 'belowBar',
            shape: supplyDemandZone.type === 'supply' ? 'arrowDown' : 'arrowUp',
            color,
            text: supplyDemandZone.type
        };
    }

    private findBarIndexAtOrAfter(timestamp: UnixTimestamp): number {
        let low = 0;
        let high = this.bars.length - 1;
        let result = this.bars.length;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const bar = this.bars[mid];

            if (bar.t >= timestamp) {
                result = mid;
                high = mid - 1;
            } else {
                low = mid + 1;
            }
        }

        return result;
    }

    private getSupplyDemandZoneRenderRange(
        startTimestamp: UnixTimestamp
    ): { startTimestamp: UnixTimestamp; endTimestamp: UnixTimestamp } | null {
        if (!this.bars.length) return null;

        const startIndex = this.findBarIndexAtOrAfter(startTimestamp);
        if (startIndex >= this.bars.length) return null;

        const endIndex = Math.min(startIndex + SUPPLY_DEMAND_ZONE_RENDER_BAR_SPAN - 1, this.bars.length - 1);
        const startBar = this.bars[startIndex];
        const endBar = this.bars[endIndex];

        if (!startBar || !endBar) return null;

        return {
            startTimestamp: startBar.t,
            endTimestamp: endBar.t
        };
    }

    private shouldPreferCurrentActiveSupplyDemandZones(): boolean {
        return HIGHER_INTRADAY_SUPPLY_DEMAND_DURATIONS.has(this.chartSettings.duration);
    }

    private getSupplyDemandZoneDistanceFromCurrentPrice(zone: SupplyDemandZone, currentPrice: number): number {
        if (currentPrice >= zone.priceLow && currentPrice <= zone.priceHigh) {
            return 0;
        }

        return Math.min(Math.abs(zone.priceLow - currentPrice), Math.abs(zone.priceHigh - currentPrice));
    }

    private prioritizeHigherIntradaySupplyDemandZones(zones: SupplyDemandZone[]): SupplyDemandZone[] {
        const currentPrice = this.bars.at(-1)?.c;

        if (!currentPrice) {
            return zones;
        }

        const sortZonesByRelevance = (left: SupplyDemandZone, right: SupplyDemandZone): number => {
            const distanceDifference =
                this.getSupplyDemandZoneDistanceFromCurrentPrice(left, currentPrice) -
                this.getSupplyDemandZoneDistanceFromCurrentPrice(right, currentPrice);

            if (distanceDifference !== 0) {
                return distanceDifference;
            }

            return right.startTimestamp - left.startTimestamp;
        };

        const selectRelevantZonesForSide = (side: SupplyDemandZone['type']): SupplyDemandZone[] => {
            const sideZones = zones.filter((zone) => zone.type === side).sort(sortZonesByRelevance);

            const nearbyZones = sideZones.filter(
                (zone) =>
                    this.getSupplyDemandZoneDistanceFromCurrentPrice(zone, currentPrice) / currentPrice <=
                    HIGHER_INTRADAY_SUPPLY_DEMAND_MAX_DISTANCE_PCT
            );

            if (nearbyZones.length > 0) {
                return nearbyZones.slice(0, MAX_HIGHER_INTRADAY_SUPPLY_DEMAND_ZONES_PER_SIDE);
            }

            return sideZones.slice(0, 1);
        };

        const prioritizedZones = [...selectRelevantZonesForSide('supply'), ...selectRelevantZonesForSide('demand')];

        return prioritizedZones.sort((left, right) => left.startTimestamp - right.startTimestamp);
    }

    async addHighsAndLows() {
        const { entity, startDateTime, endDateTime, shouldRemoveAfterHours } = this.chartSettings;

        try {
            const highsAndLowsResponse = await trpc.entity.fetchHighsAndLows.query({
                entityId: entity.id,
                entityType: entity.entityType,
                startDateTime,
                endDateTime,
                duration: this.chartSettings.duration,
                shouldRemoveAfterHours
            });

            if (!highsAndLowsResponse?.success) {
                console.error('Failed to fetch highs and lows:', highsAndLowsResponse?.errorMessages);
                return;
            }

            // Collect all high/low markers
            const markers: SeriesMarker<Time>[] = [];

            for (const highLow of highsAndLowsResponse.highsAndLows) {
                const markerDateTime = getDateFromUnix(highLow.t, 'ymdhms');
                const position = highLow.type === 'high' ? 'aboveBar' : 'belowBar';

                markers.push({
                    time: this.chartSettings.duration.endsWith('m')
                        ? convertEstTimestampToTradingView(getUnixTime(markerDateTime))
                        : getDate({ date: markerDateTime }),
                    position,
                    shape: 'arrowDown',
                    color: '#FFEB00',
                    text: highLow.type
                });
            }

            // Use consolidated marker system for highs and lows
            this.registerMarkers('highs-lows', markers);
        } catch (error) {
            console.error('Error fetching highs and lows:', error);
        }
    }

    removeTrendlines() {
        for (const lineSeries of this.trendlineSeries.values()) {
            this.chart?.removeSeries(lineSeries);
        }

        this.trendlineSeries.clear();
    }

    async addMicroTrends() {
        const { entity, startDateTime, endDateTime, shouldRemoveAfterHours } = this.chartSettings;

        try {
            const microtrendsResponse = await trpc.entity.fetchMicrotrends.query({
                entityId: entity.id,
                entityType: entity.entityType,
                startDateTime,
                endDateTime,
                duration: this.chartSettings.duration,
                shouldRemoveAfterHours
            });

            if (!microtrendsResponse?.success) {
                console.error('Failed to fetch microtrends:', microtrendsResponse?.errorMessages);
                return;
            }

            for (const m of microtrendsResponse.microtrends) {
                this.addMicroTrendLineSeries(m);
            }
        } catch (error) {
            console.error('Error fetching microtrends:', error);
        }
    }

    removeMicroTrends() {
        for (const lineSeries of this.microTrendSeries.values()) {
            this.chart?.removeSeries(lineSeries);
        }

        this.microTrendSeries.clear();
    }

    async addSupplyDemandZones() {
        const { entity, startDateTime, endDateTime, shouldRemoveAfterHours } = this.chartSettings;
        const shouldPreferCurrentActiveZones = this.shouldPreferCurrentActiveSupplyDemandZones();

        try {
            const supplyDemandZonesResponse = await trpc.entity.fetchSupplyDemandZones.query({
                entityId: entity.id,
                entityType: entity.entityType,
                startDateTime,
                endDateTime,
                duration: this.chartSettings.duration,
                shouldRemoveAfterHours,
                zoneSelectionMode: shouldPreferCurrentActiveZones ? 'currentActive' : 'window'
            });

            if (!supplyDemandZonesResponse?.success) {
                console.error('Failed to fetch supply demand zones:', supplyDemandZonesResponse?.errorMessages);
                return;
            }

            // Collect all markers from zones
            const markers: SeriesMarker<Time>[] = [];

            const prioritizedZones = shouldPreferCurrentActiveZones
                ? this.prioritizeHigherIntradaySupplyDemandZones(supplyDemandZonesResponse.zones)
                : supplyDemandZonesResponse.zones;

            const zonesToRender = [...prioritizedZones]
                .sort((left, right) => right.startTimestamp - left.startTimestamp)
                .slice(0, MAX_SUPPLY_DEMAND_ZONES_TO_RENDER)
                .sort((left, right) => left.startTimestamp - right.startTimestamp);

            for (const sd of zonesToRender) {
                const marker = this.addSupplyDemandZoneLineSeries(sd);

                if (marker) {
                    markers.push(marker);
                }
            }

            // Use consolidated marker system for supply/demand zones
            this.registerMarkers('supply-demand', markers);
        } catch (error) {
            console.error('Error fetching supply demand zones:', error);
        }
    }

    addCandlestickPatterns() {
        const markers: SeriesMarker<Time>[] = [];

        for (const bar of this.bars) {
            for (const pattern of bar.cs ?? []) {
                let patternName = '';

                for (const patternKey of Object.keys(CANDLESTICK_PATTERN_SUBTYPES)) {
                    const patternDetails = CANDLESTICK_PATTERN_SUBTYPES[patternKey as keyof typeof CANDLESTICK_PATTERN_SUBTYPES];

                    if (patternDetails.id === pattern) {
                        patternName = patternDetails.name;
                        break;
                    }
                }

                markers.push({
                    time: this.chartSettings.duration.endsWith('m') ? convertEstTimestampToTradingView(bar.t) : getDate({ date: bar.t }),
                    position: 'aboveBar',
                    shape: 'arrowDown',
                    color: '#595F63',
                    text: patternName
                });
            }
        }

        // Use consolidated marker system for candlestick patterns
        this.registerMarkers('candlestick-patterns', markers);
    }

    removeSupplyDemandZones() {
        for (const lineSeries of this.supplyDemandZoneSeries.values()) {
            this.chart?.removeSeries(lineSeries);
        }

        this.supplyDemandZoneSeries.clear();
    }

    private isChartPatternDurationSupported(): boolean {
        return isChartPatternDurationSupported(this.chartSettings.duration);
    }

    private getChartPatternColor(state: ChartPatternState): string {
        return CHART_PATTERN_STATE_COLORS[state];
    }

    private getChartPatternMarkerPosition(anchorRole: ChartPatternAnchor['role']): 'aboveBar' | 'belowBar' {
        return BELOW_BAR_CHART_PATTERN_ANCHORS.has(anchorRole) ? 'belowBar' : 'aboveBar';
    }

    private applyChartPatternOpacity(color: string, opacity: number): string {
        if (!color.startsWith('#') || (color.length !== 7 && color.length !== 4)) {
            return color;
        }

        const normalizedColor = color.length === 4 ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}` : color;
        const red = Number.parseInt(normalizedColor.slice(1, 3), 16);
        const green = Number.parseInt(normalizedColor.slice(3, 5), 16);
        const blue = Number.parseInt(normalizedColor.slice(5, 7), 16);

        return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
    }

    private addChartPatternLineSeries(
        key: string,
        data: SingleValueData[],
        config: { color: string; lineWidth?: 1 | 2 | 3 | 4; lineStyle?: LineStyle }
    ) {
        if (!this.chart || data.length < 2) return;

        const lineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: config.lineWidth ?? 2,
            color: config.color,
            lineStyle: config.lineStyle
        });

        lineSeries.setData(data);
        this.chartPatternSeries.set(key, lineSeries);
    }

    private addChartPatternHorizontalLevel(config: {
        key: string;
        price: number | null;
        startTimestamp: number;
        endTimestamp: number;
        color: string;
        lineStyle?: LineStyle;
    }) {
        const { key, price, startTimestamp, endTimestamp, color, lineStyle } = config;
        if (price == null) return;

        this.addChartPatternLineSeries(
            key,
            [
                { time: convertEstTimestampToTradingView(startTimestamp), value: price },
                { time: convertEstTimestampToTradingView(endTimestamp), value: price }
            ],
            { color, lineWidth: 1, lineStyle }
        );
    }

    private addChartPatternBoundaryFill(key: string, shape: ChartPatternBoundaryFillShape): void {
        if (!this.candlestickSeries) {
            return;
        }

        const existingPrimitive = this.chartPatternPrimitives.get(key);

        if (existingPrimitive) {
            this.candlestickSeries.detachPrimitive(existingPrimitive);
        }

        const primitive = new ChartPatternBoundaryFillPrimitive(shape);
        this.candlestickSeries.attachPrimitive(primitive);
        this.chartPatternPrimitives.set(key, primitive);
    }

    private isChartPatternSnapshot(
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence
    ): item is ChartPatternResolvedSnapshot {
        return 'snapshotDateTime' in item;
    }

    private getChartPatternOverlayBaseKey(item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence): string {
        if (this.isChartPatternSnapshot(item)) {
            return `${item.occurrenceId}:${item.snapshotDateTime}`;
        }

        let referenceDateTime = 'occurrence';

        if (item.lastSeenDateTime) {
            referenceDateTime = item.lastSeenDateTime;
        } else if (item.triggeredDateTime) {
            referenceDateTime = item.triggeredDateTime;
        } else if (item.formedDateTime) {
            referenceDateTime = item.formedDateTime;
        } else if (item.availableFromDateTime) {
            referenceDateTime = item.availableFromDateTime;
        }

        return `${item.occurrenceId}:${referenceDateTime}`;
    }

    private shouldRenderChartPatternTradeLevels(
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence,
        isFocusedMode: boolean
    ): boolean {
        if (!isFocusedMode) {
            return false;
        }

        if (this.isChartPatternSnapshot(item)) {
            return item.isTriggered || item.state === 'successful' || item.state === 'failed';
        }

        return item.state === 'triggered' || item.state === 'successful' || item.state === 'failed';
    }

    private getChartPatternOccurrenceRelevantTimestamp(occurrence: ChartPatternResolvedOccurrence): number | null {
        let relevantDateTime: string | null = null;

        if (occurrence.triggeredDateTime) {
            relevantDateTime = occurrence.triggeredDateTime;
        } else if (occurrence.successfulDateTime) {
            relevantDateTime = occurrence.successfulDateTime;
        } else if (occurrence.failedDateTime) {
            relevantDateTime = occurrence.failedDateTime;
        } else if (occurrence.expiredDateTime) {
            relevantDateTime = occurrence.expiredDateTime;
        } else if (occurrence.formedDateTime) {
            relevantDateTime = occurrence.formedDateTime;
        } else if (occurrence.lastSeenDateTime) {
            relevantDateTime = occurrence.lastSeenDateTime;
        } else if (occurrence.availableFromDateTime) {
            relevantDateTime = occurrence.availableFromDateTime;
        }

        return relevantDateTime ? getUnixTime(relevantDateTime) : null;
    }

    private getChartPatternMetricNumber(
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence,
        metricKey: string
    ): number | null {
        const value = item.metrics[metricKey];

        return typeof value === 'number' && Number.isFinite(value) ? value : null;
    }

    private getChartPatternFlagGeometry(
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence
    ): ChartPatternFlagGeometry | null {
        if (item.patternKey !== 'bullFlag' && item.patternKey !== 'bearFlag') {
            return null;
        }

        const poleStartTimestamp = this.getChartPatternMetricNumber(item, 'poleStartTimestamp');
        const poleStartPrice = this.getChartPatternMetricNumber(item, 'poleStartPrice');
        const poleEndTimestamp = this.getChartPatternMetricNumber(item, 'poleEndTimestamp');
        const poleEndPrice = this.getChartPatternMetricNumber(item, 'poleEndPrice');
        const flagStartTimestamp = this.getChartPatternMetricNumber(item, 'flagStartTimestamp');
        const flagEndTimestamp = this.getChartPatternMetricNumber(item, 'flagEndTimestamp');
        const flagStartUpperPrice = this.getChartPatternMetricNumber(item, 'flagStartUpperPrice');
        const flagEndUpperPrice = this.getChartPatternMetricNumber(item, 'flagEndUpperPrice');
        const flagStartLowerPrice = this.getChartPatternMetricNumber(item, 'flagStartLowerPrice');
        const flagEndLowerPrice = this.getChartPatternMetricNumber(item, 'flagEndLowerPrice');

        if (
            poleStartTimestamp == null ||
            poleStartPrice == null ||
            poleEndTimestamp == null ||
            poleEndPrice == null ||
            flagStartTimestamp == null ||
            flagEndTimestamp == null ||
            flagStartUpperPrice == null ||
            flagEndUpperPrice == null ||
            flagStartLowerPrice == null ||
            flagEndLowerPrice == null
        ) {
            return null;
        }

        return {
            poleStartTimestamp,
            poleStartPrice,
            poleEndTimestamp,
            poleEndPrice,
            flagStartTimestamp,
            flagEndTimestamp,
            flagStartUpperPrice,
            flagEndUpperPrice,
            flagStartLowerPrice,
            flagEndLowerPrice
        };
    }

    private getChartPatternBoundaryGeometry(
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence
    ): ChartPatternBoundaryGeometry | null {
        const upperStartAnchor = item.anchors.find((anchor) => anchor.role === ChartPatternAnchorRole.upperStart);
        const upperEndAnchor = item.anchors.find((anchor) => anchor.role === ChartPatternAnchorRole.upperEnd);
        const lowerStartAnchor = item.anchors.find((anchor) => anchor.role === ChartPatternAnchorRole.lowerStart);
        const lowerEndAnchor = item.anchors.find((anchor) => anchor.role === ChartPatternAnchorRole.lowerEnd);

        if (!upperStartAnchor || !upperEndAnchor || !lowerStartAnchor || !lowerEndAnchor) {
            return null;
        }

        return {
            upperStartTimestamp: upperStartAnchor.timestamp,
            upperStartPrice: upperStartAnchor.price,
            upperEndTimestamp: upperEndAnchor.timestamp,
            upperEndPrice: upperEndAnchor.price,
            lowerStartTimestamp: lowerStartAnchor.timestamp,
            lowerStartPrice: lowerStartAnchor.price,
            lowerEndTimestamp: lowerEndAnchor.timestamp,
            lowerEndPrice: lowerEndAnchor.price
        };
    }

    private getHeadShouldersNecklineGeometry(
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence
    ): ChartPatternSegmentGeometry | null {
        let startRole: ChartPatternAnchorRole | null = null;
        let endRole: ChartPatternAnchorRole | null = null;

        if (item.patternKey === 'headShoulders') {
            startRole = ChartPatternAnchorRole.trough1;
            endRole = ChartPatternAnchorRole.trough2;
        } else if (item.patternKey === 'inverseHeadShoulders') {
            startRole = ChartPatternAnchorRole.high1;
            endRole = ChartPatternAnchorRole.high2;
        }

        if (startRole == null || endRole == null) {
            return null;
        }

        const startAnchor = item.anchors.find((anchor) => anchor.role === startRole);
        const endAnchor = item.anchors.find((anchor) => anchor.role === endRole);

        if (!startAnchor || !endAnchor) {
            return null;
        }

        return {
            startTimestamp: startAnchor.timestamp,
            startPrice: startAnchor.price,
            endTimestamp: endAnchor.timestamp,
            endPrice: endAnchor.price
        };
    }

    private projectChartPatternBoundaryPrice(config: {
        anchorEndPrice: number;
        anchorEndTimestamp: number;
        anchorStartPrice: number;
        anchorStartTimestamp: number;
        targetTimestamp: number;
    }): number {
        const { anchorEndPrice, anchorEndTimestamp, anchorStartPrice, anchorStartTimestamp, targetTimestamp } = config;

        if (anchorEndTimestamp === anchorStartTimestamp) {
            return anchorEndPrice;
        }

        const slopePerSecond = (anchorEndPrice - anchorStartPrice) / (anchorEndTimestamp - anchorStartTimestamp);
        return anchorStartPrice + slopePerSecond * (targetTimestamp - anchorStartTimestamp);
    }

    private getChartPatternProjectedBoundaryWindow(config: {
        anchors: ChartPatternAnchor[];
        boundaryGeometry: ChartPatternBoundaryGeometry;
    }): ChartPatternProjectedBoundaryWindow | null {
        const { anchors, boundaryGeometry } = config;
        const breakoutAnchor = anchors.find((anchor) => anchor.role === ChartPatternAnchorRole.breakout);
        const sharedStartTimestamp = Math.max(boundaryGeometry.upperStartTimestamp, boundaryGeometry.lowerStartTimestamp);
        const earliestValidEndTimestamp = Math.max(boundaryGeometry.upperEndTimestamp, boundaryGeometry.lowerEndTimestamp);
        const preferredEndTimestamp = breakoutAnchor?.timestamp ?? earliestValidEndTimestamp;
        const sharedEndTimestamp = Math.max(earliestValidEndTimestamp, preferredEndTimestamp);

        if (sharedEndTimestamp <= sharedStartTimestamp) {
            return null;
        }

        return {
            startTimestamp: sharedStartTimestamp,
            endTimestamp: sharedEndTimestamp
        };
    }

    private getChartPatternProjectedBoundaryShape(config: {
        anchors: ChartPatternAnchor[];
        boundaryGeometry: ChartPatternBoundaryGeometry;
    }): ChartPatternBoundaryFillShape | null {
        const { anchors, boundaryGeometry } = config;

        const projectedBoundaryWindow = this.getChartPatternProjectedBoundaryWindow({
            anchors,
            boundaryGeometry
        });

        if (projectedBoundaryWindow == null) {
            return null;
        }

        return {
            startTimestamp: projectedBoundaryWindow.startTimestamp,
            endTimestamp: projectedBoundaryWindow.endTimestamp,
            upperStartPrice: this.projectChartPatternBoundaryPrice({
                anchorStartTimestamp: boundaryGeometry.upperStartTimestamp,
                anchorStartPrice: boundaryGeometry.upperStartPrice,
                anchorEndTimestamp: boundaryGeometry.upperEndTimestamp,
                anchorEndPrice: boundaryGeometry.upperEndPrice,
                targetTimestamp: projectedBoundaryWindow.startTimestamp
            }),
            upperEndPrice: this.projectChartPatternBoundaryPrice({
                anchorStartTimestamp: boundaryGeometry.upperStartTimestamp,
                anchorStartPrice: boundaryGeometry.upperStartPrice,
                anchorEndTimestamp: boundaryGeometry.upperEndTimestamp,
                anchorEndPrice: boundaryGeometry.upperEndPrice,
                targetTimestamp: projectedBoundaryWindow.endTimestamp
            }),
            lowerStartPrice: this.projectChartPatternBoundaryPrice({
                anchorStartTimestamp: boundaryGeometry.lowerStartTimestamp,
                anchorStartPrice: boundaryGeometry.lowerStartPrice,
                anchorEndTimestamp: boundaryGeometry.lowerEndTimestamp,
                anchorEndPrice: boundaryGeometry.lowerEndPrice,
                targetTimestamp: projectedBoundaryWindow.startTimestamp
            }),
            lowerEndPrice: this.projectChartPatternBoundaryPrice({
                anchorStartTimestamp: boundaryGeometry.lowerStartTimestamp,
                anchorStartPrice: boundaryGeometry.lowerStartPrice,
                anchorEndTimestamp: boundaryGeometry.lowerEndTimestamp,
                anchorEndPrice: boundaryGeometry.lowerEndPrice,
                targetTimestamp: projectedBoundaryWindow.endTimestamp
            }),
            fillColor: CHART_PATTERN_STATE_COLORS.triggered
        };
    }

    private shouldRenderChartPatternBoundaryFill(): boolean {
        return this.chartSettings.chartPatternFocusPaddingPreset === 'audit';
    }

    private addChartPatternOverlay(
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence,
        config: { chartPatternDisplayMode: ChartPatternDisplayMode; isFocusedMode: boolean },
        emphasis: 'primary' | 'secondary' = 'primary'
    ): SeriesMarker<Time>[] {
        const anchors = [...item.anchors].sort((a, b) => a.barIndex - b.barIndex);
        if (!anchors.length) return [];

        const { chartPatternDisplayMode, isFocusedMode } = config;
        const patternColorBase = this.getChartPatternColor(item.state);
        const patternColor = emphasis === 'primary' ? patternColorBase : this.applyChartPatternOpacity(patternColorBase, 0.35);
        const baseKey = this.getChartPatternOverlayBaseKey(item);
        const firstAnchor = anchors[0];

        const horizontalEndTimestamp = this.getChartPatternHorizontalEndTimestamp({
            item,
            anchors,
            isFocusedMode
        });

        const includeMarkers = emphasis === 'primary';

        const shouldRenderTradeLevels = this.shouldRenderChartPatternTradeLevels(item, isFocusedMode);

        const flagGeometry = this.getChartPatternFlagGeometry(item);
        const boundaryGeometry = flagGeometry == null ? this.getChartPatternBoundaryGeometry(item) : null;

        const headShouldersNecklineGeometry =
            flagGeometry == null && boundaryGeometry == null ? this.getHeadShouldersNecklineGeometry(item) : null;

        const shouldRenderResistanceLevel = flagGeometry == null;

        const resistanceBreakoutMerged =
            shouldRenderResistanceLevel && this.shouldMergeChartPatternLevels(item.resistancePrice, item.breakoutPrice);

        const effectiveResistanceColor = resistanceBreakoutMerged && shouldRenderTradeLevels ? '#22C55E' : '#F59E0B';

        const markers: SeriesMarker<Time>[] =
            includeMarkers && this.chartSettings.shouldRenderChartPatternAnchorMarkers
                ? anchors.map((anchor) => {
                      const position = this.getChartPatternMarkerPosition(anchor.role);

                      const marker: SeriesMarker<Time> = {
                          time: convertEstTimestampToTradingView(anchor.timestamp),
                          position,
                          shape: 'circle',
                          color: patternColor
                      };

                      const markerText = this.getChartPatternAnchorMarkerText({
                          patternKey: item.patternKey,
                          anchorRole: anchor.role,
                          chartPatternDisplayMode,
                          anchorIndex: anchors.indexOf(anchor),
                          anchors
                      });

                      if (markerText) {
                          marker.text = markerText;
                      }

                      return marker;
                  })
                : [];

        if (flagGeometry) {
            const geometryLineStyle = emphasis === 'primary' ? undefined : LineStyle.Dotted;
            const geometryLineWidth = emphasis === 'primary' ? (isFocusedMode ? 3 : 2) : 1;

            this.addChartPatternLineSeries(
                `${baseKey}:pole`,
                [
                    {
                        time: convertEstTimestampToTradingView(flagGeometry.poleStartTimestamp),
                        value: flagGeometry.poleStartPrice
                    },
                    {
                        time: convertEstTimestampToTradingView(flagGeometry.poleEndTimestamp),
                        value: flagGeometry.poleEndPrice
                    }
                ],
                {
                    color: patternColor,
                    lineWidth: geometryLineWidth,
                    lineStyle: geometryLineStyle
                }
            );

            this.addChartPatternLineSeries(
                `${baseKey}:flagUpper`,
                [
                    {
                        time: convertEstTimestampToTradingView(flagGeometry.flagStartTimestamp),
                        value: flagGeometry.flagStartUpperPrice
                    },
                    {
                        time: convertEstTimestampToTradingView(flagGeometry.flagEndTimestamp),
                        value: flagGeometry.flagEndUpperPrice
                    }
                ],
                {
                    color: patternColor,
                    lineWidth: geometryLineWidth,
                    lineStyle: geometryLineStyle
                }
            );

            this.addChartPatternLineSeries(
                `${baseKey}:flagLower`,
                [
                    {
                        time: convertEstTimestampToTradingView(flagGeometry.flagStartTimestamp),
                        value: flagGeometry.flagStartLowerPrice
                    },
                    {
                        time: convertEstTimestampToTradingView(flagGeometry.flagEndTimestamp),
                        value: flagGeometry.flagEndLowerPrice
                    }
                ],
                {
                    color: patternColor,
                    lineWidth: geometryLineWidth,
                    lineStyle: geometryLineStyle
                }
            );
        } else if (boundaryGeometry) {
            const boundaryLineStyle = emphasis === 'primary' ? undefined : LineStyle.Dotted;
            const boundaryLineWidth = emphasis === 'primary' ? (isFocusedMode ? 3 : 2) : 1;

            const projectedBoundaryShape = this.getChartPatternProjectedBoundaryShape({
                anchors,
                boundaryGeometry
            });

            const upperBoundaryData =
                projectedBoundaryShape == null
                    ? [
                          {
                              time: convertEstTimestampToTradingView(boundaryGeometry.upperStartTimestamp),
                              value: boundaryGeometry.upperStartPrice
                          },
                          {
                              time: convertEstTimestampToTradingView(boundaryGeometry.upperEndTimestamp),
                              value: boundaryGeometry.upperEndPrice
                          }
                      ]
                    : [
                          {
                              time: convertEstTimestampToTradingView(projectedBoundaryShape.startTimestamp),
                              value: projectedBoundaryShape.upperStartPrice
                          },
                          {
                              time: convertEstTimestampToTradingView(projectedBoundaryShape.endTimestamp),
                              value: projectedBoundaryShape.upperEndPrice
                          }
                      ];

            const lowerBoundaryData =
                projectedBoundaryShape == null
                    ? [
                          {
                              time: convertEstTimestampToTradingView(boundaryGeometry.lowerStartTimestamp),
                              value: boundaryGeometry.lowerStartPrice
                          },
                          {
                              time: convertEstTimestampToTradingView(boundaryGeometry.lowerEndTimestamp),
                              value: boundaryGeometry.lowerEndPrice
                          }
                      ]
                    : [
                          {
                              time: convertEstTimestampToTradingView(projectedBoundaryShape.startTimestamp),
                              value: projectedBoundaryShape.lowerStartPrice
                          },
                          {
                              time: convertEstTimestampToTradingView(projectedBoundaryShape.endTimestamp),
                              value: projectedBoundaryShape.lowerEndPrice
                          }
                      ];

            if (emphasis === 'primary' && projectedBoundaryShape && this.shouldRenderChartPatternBoundaryFill()) {
                this.addChartPatternBoundaryFill(`${baseKey}:boundaryFill`, {
                    ...projectedBoundaryShape,
                    fillColor: this.applyChartPatternOpacity(patternColorBase, 0.14)
                });
            }

            this.addChartPatternLineSeries(`${baseKey}:upperBoundary`, upperBoundaryData, {
                color: patternColor,
                lineWidth: boundaryLineWidth,
                lineStyle: boundaryLineStyle
            });

            this.addChartPatternLineSeries(`${baseKey}:lowerBoundary`, lowerBoundaryData, {
                color: patternColor,
                lineWidth: boundaryLineWidth,
                lineStyle: boundaryLineStyle
            });
        } else {
            if (headShouldersNecklineGeometry) {
                this.addChartPatternLineSeries(
                    `${baseKey}:neckline`,
                    [
                        {
                            time: convertEstTimestampToTradingView(headShouldersNecklineGeometry.startTimestamp),
                            value: headShouldersNecklineGeometry.startPrice
                        },
                        {
                            time: convertEstTimestampToTradingView(headShouldersNecklineGeometry.endTimestamp),
                            value: headShouldersNecklineGeometry.endPrice
                        }
                    ],
                    {
                        color: patternColor,
                        lineWidth: emphasis === 'primary' ? (isFocusedMode ? 3 : 2) : 1,
                        lineStyle: emphasis === 'primary' ? undefined : LineStyle.Dotted
                    }
                );
            }

            this.addChartPatternLineSeries(
                `${baseKey}:shape`,
                anchors.map((anchor) => ({
                    time: convertEstTimestampToTradingView(anchor.timestamp),
                    value: anchor.price
                })),
                {
                    color: patternColor,
                    lineWidth: emphasis === 'primary' ? (isFocusedMode ? 3 : 2) : 1,
                    lineStyle: emphasis === 'primary' ? undefined : LineStyle.Dotted
                }
            );
        }

        if (shouldRenderResistanceLevel) {
            this.addChartPatternHorizontalLevel({
                key: `${baseKey}:resistance`,
                price: item.resistancePrice,
                startTimestamp: firstAnchor.timestamp,
                endTimestamp: horizontalEndTimestamp,
                color: emphasis === 'primary' ? effectiveResistanceColor : this.applyChartPatternOpacity(effectiveResistanceColor, 0.35),
                lineStyle: LineStyle.Dashed
            });
        }

        if (!resistanceBreakoutMerged) {
            this.addChartPatternHorizontalLevel({
                key: `${baseKey}:breakout`,
                price: item.breakoutPrice,
                startTimestamp: firstAnchor.timestamp,
                endTimestamp: horizontalEndTimestamp,
                color: emphasis === 'primary' ? '#22C55E' : this.applyChartPatternOpacity('#22C55E', 0.35),
                lineStyle: LineStyle.Dashed
            });
        }

        if (shouldRenderTradeLevels) {
            this.addChartPatternHorizontalLevel({
                key: `${baseKey}:stop`,
                price: item.stopPrice,
                startTimestamp: firstAnchor.timestamp,
                endTimestamp: horizontalEndTimestamp,
                color: emphasis === 'primary' ? '#EF4444' : this.applyChartPatternOpacity('#EF4444', 0.35),
                lineStyle: LineStyle.LargeDashed
            });

            this.addChartPatternHorizontalLevel({
                key: `${baseKey}:target1`,
                price: item.target1Price,
                startTimestamp: firstAnchor.timestamp,
                endTimestamp: horizontalEndTimestamp,
                color: emphasis === 'primary' ? '#38BDF8' : this.applyChartPatternOpacity('#38BDF8', 0.35),
                lineStyle: LineStyle.Dotted
            });
        }

        return markers;
    }

    private shouldMergeChartPatternLevels(levelA: number | null, levelB: number | null): boolean {
        if (levelA == null || levelB == null) {
            return false;
        }

        const referencePrice = Math.max(Math.abs(levelA), Math.abs(levelB), 1);
        return Math.abs(levelA - levelB) / referencePrice <= CHART_PATTERN_LEVEL_MERGE_TOLERANCE_PCT;
    }

    private getChartPatternShortLabel(patternKey: string): string {
        return CHART_PATTERN_SHORT_LABELS[patternKey] ?? patternKey;
    }

    private shouldSuppressChartPatternMarkerText(): boolean {
        return this.chartSettings.chartPatternFocusPaddingPreset === 'audit';
    }

    private getChartPatternAnchorMarkerText(config: {
        patternKey: string;
        anchorRole: ChartPatternAnchorRole;
        chartPatternDisplayMode: ChartPatternDisplayMode;
        anchorIndex: number;
        anchors: ChartPatternAnchor[];
    }): string | undefined {
        if (this.shouldSuppressChartPatternMarkerText()) {
            return undefined;
        }

        if (config.chartPatternDisplayMode === 'allCurrent' || config.chartPatternDisplayMode === 'historicalOccurrences') {
            return undefined;
        }

        if (config.chartPatternDisplayMode === 'focused' || config.chartPatternDisplayMode === 'pitReplay') {
            return `${this.getChartPatternShortLabel(config.patternKey)} ${CHART_PATTERN_ANCHOR_SHORT_LABELS[config.anchorRole]}`;
        }

        if (this.shouldLabelChartPatternAnchorInSelectedMode(config)) {
            return this.getChartPatternShortLabel(config.patternKey);
        }

        return undefined;
    }

    private shouldLabelChartPatternAnchorInSelectedMode(config: {
        anchorRole: ChartPatternAnchorRole;
        anchorIndex: number;
        anchors: ChartPatternAnchor[];
    }): boolean {
        const hasBreakoutAnchor = config.anchors.some((anchor) => anchor.role === ChartPatternAnchorRole.breakout);

        if (hasBreakoutAnchor) {
            return config.anchorRole === ChartPatternAnchorRole.breakout;
        }

        const hasNecklineAnchor = config.anchors.some((anchor) => anchor.role === ChartPatternAnchorRole.neckline);

        if (hasNecklineAnchor) {
            return config.anchorRole === ChartPatternAnchorRole.neckline;
        }

        return config.anchorIndex === config.anchors.length - 1;
    }

    private getChartPatternHorizontalEndTimestamp(config: {
        item: ChartPatternResolvedSnapshot | ChartPatternResolvedOccurrence;
        anchors: ChartPatternAnchor[];
        isFocusedMode: boolean;
    }): number {
        const { item, anchors, isFocusedMode } = config;
        const firstAnchor = anchors[0];
        const lastAnchor = anchors[anchors.length - 1];
        const lastBarIndex = this.bars.length - 1;

        if (lastBarIndex < 0) {
            return lastAnchor.timestamp;
        }

        const patternWidthBars = Math.max(3, lastAnchor.barIndex - firstAnchor.barIndex);

        const paddingBars = isFocusedMode
            ? Math.max(8, Math.round(patternWidthBars * 0.5))
            : Math.max(4, Math.round(patternWidthBars * 0.25));

        const candidateEndIndices = [Math.max(0, Math.min(lastBarIndex, lastAnchor.barIndex))];

        if (this.isChartPatternSnapshot(item)) {
            if (item.barsSinceLastEvent != null && item.barsSinceLastEvent <= lastBarIndex) {
                candidateEndIndices.push(lastBarIndex - item.barsSinceLastEvent);
            }

            if (item.barsSinceTriggered != null && item.barsSinceTriggered <= lastBarIndex) {
                candidateEndIndices.push(lastBarIndex - item.barsSinceTriggered);
            }
        } else {
            const relevantTimestamp = this.getChartPatternOccurrenceRelevantTimestamp(item);
            const relevantIndex = relevantTimestamp == null ? null : this.barsTimestampMap.get(relevantTimestamp);

            if (relevantIndex != null) {
                candidateEndIndices.push(Math.max(0, Math.min(lastBarIndex, relevantIndex)));
            }
        }

        const anchorWindowEndIndex = Math.max(...candidateEndIndices);
        const projectedEndIndex = Math.min(lastBarIndex, anchorWindowEndIndex + paddingBars);
        return this.bars[projectedEndIndex]?.t ?? lastAnchor.timestamp;
    }

    private getChartPatternEventColor(eventCategory: ChartPatternResolvedEvent['eventCategory']): string {
        return CHART_PATTERN_EVENT_COLORS[eventCategory];
    }

    private getChartPatternEventMarkerPosition(event: ChartPatternResolvedEvent): 'aboveBar' | 'belowBar' {
        if (event.eventCategory === 'failed' || event.eventCategory === 'expired') {
            return 'aboveBar';
        }

        return event.direction === 'bearish' ? 'aboveBar' : 'belowBar';
    }

    private getChartPatternEventMarkerShape(event: ChartPatternResolvedEvent): 'circle' | 'square' | 'arrowUp' | 'arrowDown' {
        if (event.eventCategory === 'breakoutConfirmed') {
            return event.direction === 'bearish' ? 'arrowDown' : 'arrowUp';
        }

        if (event.eventCategory === 'failed' || event.eventCategory === 'expired') {
            return 'square';
        }

        return 'circle';
    }

    private getChartPatternEventMarkerText(config: {
        chartPatternDisplayMode: ChartPatternDisplayMode;
        event: ChartPatternResolvedEvent;
    }): string | undefined {
        if (this.shouldSuppressChartPatternMarkerText()) {
            return undefined;
        }

        if (config.chartPatternDisplayMode === 'allCurrent' && !this.chartSettings.chartPatternKeyFilter) {
            return undefined;
        }

        if (
            (config.chartPatternDisplayMode === 'allCurrent' ||
                (config.chartPatternDisplayMode === 'historicalOccurrences' && !this.chartSettings.chartPatternKeyFilter)) &&
            config.event.eventCategory !== ChartPatternEventCategory.breakoutConfirmed &&
            config.event.eventCategory !== ChartPatternEventCategory.failed &&
            config.event.eventCategory !== ChartPatternEventCategory.expired
        ) {
            return undefined;
        }

        return `${this.getChartPatternShortLabel(config.event.patternKey)} ${this.getChartPatternEventShortLabel(config.event)}`;
    }

    private getChartPatternStateTimelineLabel(snapshot: ChartPatternResolvedSnapshot): string {
        if (snapshot.phaseKey) {
            const phaseLabel = this.getChartPatternPhaseShortLabel(snapshot);

            if (phaseLabel) {
                return phaseLabel;
            }
        }

        return snapshot.state.charAt(0).toUpperCase() + snapshot.state.slice(1);
    }

    private getChartPatternEventShortLabel(event: ChartPatternResolvedEvent): string {
        if (event.eventCategory === ChartPatternEventCategory.breakoutConfirmed) {
            return event.direction === 'bearish' ? 'Breakdown' : 'Breakout';
        }

        return CHART_PATTERN_EVENT_SHORT_LABELS[event.eventCategory];
    }

    private getChartPatternPhaseShortLabel(snapshot: ChartPatternResolvedSnapshot): string | null {
        if (!snapshot.phaseKey) {
            return null;
        }

        if ((snapshot.phaseKey === 'postBreakout' || snapshot.phaseKey === 'postBreakdown') && snapshot.direction === 'bearish') {
            return 'Breakdown';
        }

        return CHART_PATTERN_PHASE_SHORT_LABELS[snapshot.phaseKey] ?? null;
    }

    private getChartPatternStateMarkerPosition(snapshot: ChartPatternResolvedSnapshot): 'aboveBar' | 'belowBar' {
        return snapshot.direction === 'bearish' ? 'aboveBar' : 'belowBar';
    }

    private getSortedChartPatternSnapshotsByOccurrence(
        snapshots: ChartPatternResolvedSnapshot[]
    ): Map<number, ChartPatternResolvedSnapshot[]> {
        const snapshotsByOccurrence = new Map<number, ChartPatternResolvedSnapshot[]>();

        for (const snapshot of snapshots) {
            const existing = snapshotsByOccurrence.get(snapshot.occurrenceId);

            if (existing) {
                existing.push(snapshot);
            } else {
                snapshotsByOccurrence.set(snapshot.occurrenceId, [snapshot]);
            }
        }

        for (const occurrenceSnapshots of snapshotsByOccurrence.values()) {
            occurrenceSnapshots.sort((left, right) => getUnixTime(left.snapshotDateTime) - getUnixTime(right.snapshotDateTime));
        }

        return snapshotsByOccurrence;
    }

    private getChartPatternRepresentativeSnapshotForAllCurrent(
        occurrenceSnapshots: ChartPatternResolvedSnapshot[]
    ): ChartPatternResolvedSnapshot | null {
        const latestSnapshot = last(occurrenceSnapshots) ?? null;

        if (!latestSnapshot) {
            return null;
        }

        if (latestSnapshot.state === 'triggered' || latestSnapshot.state === 'successful') {
            return (
                occurrenceSnapshots.find(
                    (snapshot) =>
                        snapshot.lastEventCategory === ChartPatternEventCategory.breakoutConfirmed ||
                        snapshot.state === 'triggered' ||
                        snapshot.state === 'successful'
                ) ?? latestSnapshot
            );
        }

        if (latestSnapshot.state === 'failed') {
            return (
                occurrenceSnapshots.find(
                    (snapshot) => snapshot.lastEventCategory === ChartPatternEventCategory.failed || snapshot.state === 'failed'
                ) ?? latestSnapshot
            );
        }

        if (latestSnapshot.state === 'expired') {
            return (
                occurrenceSnapshots.find(
                    (snapshot) => snapshot.lastEventCategory === ChartPatternEventCategory.expired || snapshot.state === 'expired'
                ) ?? latestSnapshot
            );
        }

        return latestSnapshot;
    }

    private shouldRenderAllCurrentChartPatternStateMarker(latestSnapshot: ChartPatternResolvedSnapshot): boolean {
        if (
            latestSnapshot.state === 'triggered' ||
            latestSnapshot.state === 'successful' ||
            latestSnapshot.state === 'failed' ||
            latestSnapshot.state === 'expired'
        ) {
            return latestSnapshot.lastEventCategory == null;
        }

        return true;
    }

    private getChartPatternAllCurrentStateMarkerText(snapshot: ChartPatternResolvedSnapshot): string | undefined {
        if (this.shouldSuppressChartPatternMarkerText()) {
            return undefined;
        }

        const shouldLabel = this.chartSettings.chartPatternKeyFilter != null || snapshot.state === 'forming' || snapshot.state === 'formed';

        if (!shouldLabel) {
            return undefined;
        }

        return `${this.getChartPatternShortLabel(snapshot.patternKey)} ${this.getChartPatternStateTimelineLabel(snapshot)}`;
    }

    private getChartPatternRelevantEndIndex(config: {
        focusedSnapshot: ChartPatternResolvedSnapshot;
        historySnapshots: ChartPatternResolvedSnapshot[];
        lastAnchorBarIndex: number;
    }): number {
        const { focusedSnapshot, historySnapshots, lastAnchorBarIndex } = config;
        const lastBarIndex = this.bars.length - 1;
        const latestSnapshot = last(historySnapshots) ?? focusedSnapshot;
        const latestSnapshotIndex = this.barsTimestampMap.get(getUnixTime(latestSnapshot.snapshotDateTime)) ?? lastAnchorBarIndex;
        const hasOpenLifecycle = latestSnapshot.state === 'forming' || latestSnapshot.state === 'formed';

        if (hasOpenLifecycle) {
            return Math.max(lastAnchorBarIndex, Math.min(lastBarIndex, latestSnapshotIndex));
        }

        const representativeSnapshot =
            this.getChartPatternRepresentativeSnapshotForAllCurrent(historySnapshots.length ? historySnapshots : [focusedSnapshot]) ??
            latestSnapshot;

        const representativeSnapshotIndex = this.barsTimestampMap.get(getUnixTime(representativeSnapshot.snapshotDateTime));

        if (representativeSnapshotIndex != null) {
            return Math.max(lastAnchorBarIndex, Math.min(lastBarIndex, representativeSnapshotIndex));
        }

        if (latestSnapshot.barsSinceLastEvent != null && latestSnapshot.barsSinceLastEvent <= lastBarIndex) {
            return Math.max(lastAnchorBarIndex, lastBarIndex - latestSnapshot.barsSinceLastEvent);
        }

        if (latestSnapshot.barsSinceTriggered != null && latestSnapshot.barsSinceTriggered <= lastBarIndex) {
            return Math.max(lastAnchorBarIndex, lastBarIndex - latestSnapshot.barsSinceTriggered);
        }

        return Math.max(lastAnchorBarIndex, Math.min(lastBarIndex, latestSnapshotIndex));
    }

    private buildChartPatternStateMarkers(config: {
        chartPatternDisplayMode: ChartPatternDisplayMode;
        snapshots: ChartPatternResolvedSnapshot[];
    }): SeriesMarker<Time>[] {
        const { chartPatternDisplayMode, snapshots } = config;

        if (snapshots.length === 0 || chartPatternDisplayMode === 'historicalOccurrences') {
            return [];
        }

        const snapshotsByOccurrence = this.getSortedChartPatternSnapshotsByOccurrence(snapshots);

        if (chartPatternDisplayMode === 'allCurrent') {
            const markers: SeriesMarker<Time>[] = [];

            for (const occurrenceSnapshots of snapshotsByOccurrence.values()) {
                const latestSnapshot = last(occurrenceSnapshots);

                if (!latestSnapshot || !this.shouldRenderAllCurrentChartPatternStateMarker(latestSnapshot)) {
                    continue;
                }

                const representativeSnapshot =
                    this.getChartPatternRepresentativeSnapshotForAllCurrent(occurrenceSnapshots) ?? latestSnapshot;

                const marker: SeriesMarker<Time> = {
                    time: convertEstTimestampToTradingView(getUnixTime(representativeSnapshot.snapshotDateTime)),
                    position: this.getChartPatternStateMarkerPosition(representativeSnapshot),
                    shape: 'circle',
                    color: this.getChartPatternColor(latestSnapshot.state)
                };

                const markerText = this.getChartPatternAllCurrentStateMarkerText(representativeSnapshot);

                if (markerText) {
                    marker.text = markerText;
                }

                markers.push(marker);
            }

            return markers;
        }

        const markers: SeriesMarker<Time>[] = [];

        for (const occurrenceSnapshots of snapshotsByOccurrence.values()) {
            occurrenceSnapshots.sort((left, right) => getUnixTime(left.snapshotDateTime) - getUnixTime(right.snapshotDateTime));

            for (const [index, snapshot] of occurrenceSnapshots.entries()) {
                const previous = index > 0 ? occurrenceSnapshots[index - 1] : null;

                const isTransition =
                    previous?.state !== snapshot.state ||
                    previous.phaseKey !== snapshot.phaseKey ||
                    previous.lastEventKey !== snapshot.lastEventKey;

                const shouldRenderMarker =
                    chartPatternDisplayMode === 'focused' || chartPatternDisplayMode === 'pitReplay' ? true : isTransition;

                if (!shouldRenderMarker) {
                    continue;
                }

                const isLastSnapshot = index === occurrenceSnapshots.length - 1;

                const marker: SeriesMarker<Time> = {
                    time: convertEstTimestampToTradingView(getUnixTime(snapshot.snapshotDateTime)),
                    position: this.getChartPatternStateMarkerPosition(snapshot),
                    shape: isTransition ? 'square' : 'circle',
                    color: this.getChartPatternColor(snapshot.state)
                };

                if ((isTransition || isLastSnapshot) && !this.shouldSuppressChartPatternMarkerText()) {
                    marker.text = `${this.getChartPatternShortLabel(snapshot.patternKey)} ${this.getChartPatternStateTimelineLabel(snapshot)}`;
                }

                markers.push(marker);
            }
        }

        return markers;
    }

    private applyFocusedChartPatternVisibleRange(): void {
        const {
            chartPatternDisplayMode = 'selected',
            focusedChartPatternOccurrenceId,
            shouldAutoZoomFocusedChartPattern
        } = this.chartSettings;

        if (
            !this.chart ||
            (chartPatternDisplayMode !== 'focused' && chartPatternDisplayMode !== 'pitReplay') ||
            focusedChartPatternOccurrenceId == null ||
            this.bars.length === 0 ||
            !shouldAutoZoomFocusedChartPattern
        ) {
            return;
        }

        const focusSnapshots = chartPatternDisplayMode === 'pitReplay' ? this.chartPatternPitReplaySnapshots : this.chartPatternSnapshots;

        const focusSnapshotSeries =
            chartPatternDisplayMode === 'pitReplay' ? this.chartPatternPitReplaySnapshots : this.chartPatternSnapshotSeries;

        const focusedSnapshot = focusSnapshots.find((snapshot) => snapshot.occurrenceId === focusedChartPatternOccurrenceId);

        const focusedOccurrence = this.chartPatternHistoricalOccurrences.find(
            (occurrence) => occurrence.occurrenceId === focusedChartPatternOccurrenceId
        );

        const focusAnchorsSource = focusedSnapshot ?? focusedOccurrence;

        if (!focusAnchorsSource?.anchors.length) {
            return;
        }

        const anchors = [...focusAnchorsSource.anchors].sort((left, right) => left.barIndex - right.barIndex);
        const firstAnchor = anchors[0];
        const lastAnchor = anchors[anchors.length - 1];

        const historySnapshots = focusSnapshotSeries
            .filter((snapshot) => snapshot.occurrenceId === focusedChartPatternOccurrenceId)
            .sort((left, right) => getUnixTime(left.snapshotDateTime) - getUnixTime(right.snapshotDateTime));

        const lastBarIndex = this.bars.length - 1;
        const startIndex = Math.max(0, Math.min(lastBarIndex, firstAnchor.barIndex));

        const relevantEndIndex =
            focusedSnapshot != null
                ? this.getChartPatternRelevantEndIndex({
                      focusedSnapshot,
                      historySnapshots,
                      lastAnchorBarIndex: lastAnchor.barIndex
                  })
                : (() => {
                      const relevantTimestamp = focusedOccurrence
                          ? this.getChartPatternOccurrenceRelevantTimestamp(focusedOccurrence)
                          : null;

                      const relevantIndex = relevantTimestamp == null ? null : this.barsTimestampMap.get(relevantTimestamp);
                      return relevantIndex == null ? lastAnchor.barIndex : Math.max(lastAnchor.barIndex, relevantIndex);
                  })();

        const endIndex = Math.max(startIndex, Math.min(lastBarIndex, relevantEndIndex));
        const patternWidthBars = Math.max(8, endIndex - startIndex);
        const isAuditFocusPadding = this.chartSettings.chartPatternFocusPaddingPreset === 'audit';

        const leftPaddingBars = isAuditFocusPadding
            ? Math.max(20, Math.round(patternWidthBars * 1.1))
            : Math.max(12, Math.round(patternWidthBars * 0.5));

        const rightPaddingBars = isAuditFocusPadding
            ? Math.max(24, Math.round(patternWidthBars * 1.35))
            : Math.max(16, Math.round(patternWidthBars * 0.75));

        const visibleFromIndex = Math.max(0, startIndex - leftPaddingBars);
        const visibleToIndex = Math.min(lastBarIndex, endIndex + rightPaddingBars);
        const fromBar = this.bars[visibleFromIndex];
        const toBar = this.bars[visibleToIndex];

        if (!fromBar || !toBar) {
            return;
        }

        this.chart.timeScale().applyOptions({
            rightOffset: 12
        });

        this.chart.timeScale().setVisibleRange({
            from: convertEstTimestampToTradingView(fromBar.t),
            to: convertEstTimestampToTradingView(toBar.t)
        });
    }

    private buildChartPatternEventMarkers(config: {
        chartPatternDisplayMode: ChartPatternDisplayMode;
        events: ChartPatternResolvedEvent[];
    }): SeriesMarker<Time>[] {
        const { chartPatternDisplayMode } = config;

        const events =
            chartPatternDisplayMode === 'focused' ||
            chartPatternDisplayMode === 'pitReplay' ||
            chartPatternDisplayMode === 'historicalOccurrences'
                ? config.events
                : this.getMostRecentChartPatternEventsByOccurrence(config.events);

        return events.map((event) => {
            const marker: SeriesMarker<Time> = {
                time: convertEstTimestampToTradingView(getUnixTime(event.eventDateTime)),
                position: this.getChartPatternEventMarkerPosition(event),
                shape: this.getChartPatternEventMarkerShape(event),
                color: this.getChartPatternEventColor(event.eventCategory)
            };

            const markerText = this.getChartPatternEventMarkerText({
                chartPatternDisplayMode,
                event
            });

            if (markerText) {
                marker.text = markerText;
            }

            return marker;
        });
    }

    private getMostRecentChartPatternEventsByOccurrence(events: ChartPatternResolvedEvent[]): ChartPatternResolvedEvent[] {
        const latestByOccurrence = new Map<number, ChartPatternResolvedEvent>();

        for (const event of events) {
            const existing = latestByOccurrence.get(event.occurrenceId);

            if (!existing || getUnixTime(event.eventDateTime) > getUnixTime(existing.eventDateTime)) {
                latestByOccurrence.set(event.occurrenceId, event);
            }
        }

        return [...latestByOccurrence.values()].sort((left, right) => getUnixTime(left.eventDateTime) - getUnixTime(right.eventDateTime));
    }

    addChartPatterns() {
        const {
            entity,
            duration,
            chartPatternDisplayMode = 'selected',
            chartPatternKeyFilter,
            focusedChartPatternOccurrenceId,
            chartPatternOccurrenceIdsOverride
        } = this.chartSettings;

        const isFocusedMode = chartPatternDisplayMode === 'focused';
        const occurrenceIdsOverride = chartPatternOccurrenceIdsOverride ? new Set(chartPatternOccurrenceIdsOverride) : null;

        this.removeChartPatterns();

        if (entity.entityType !== 'Asset' || !this.isChartPatternDurationSupported()) return;

        try {
            if (chartPatternDisplayMode === 'historicalOccurrences') {
                const occurrencesToRender = getChartPatternHistoricalOccurrencesForDisplay({
                    occurrences: this.chartPatternHistoricalOccurrences,
                    duration,
                    chartPatternKeyFilter
                }).filter((occurrence) => (occurrenceIdsOverride ? occurrenceIdsOverride.has(occurrence.occurrenceId) : true));

                if (!occurrencesToRender.length) return;

                const markers: SeriesMarker<Time>[] = [];

                for (const occurrence of occurrencesToRender) {
                    markers.push(
                        ...this.addChartPatternOverlay(
                            occurrence,
                            {
                                chartPatternDisplayMode,
                                isFocusedMode: false
                            },
                            'primary'
                        )
                    );
                }

                if (this.shouldRenderChartPatternEventMarkers) {
                    markers.push(
                        ...this.buildChartPatternEventMarkers({
                            chartPatternDisplayMode,
                            events: getChartPatternHistoricalEventsForDisplay({
                                events: this.chartPatternHistoricalEvents,
                                duration,
                                chartPatternKeyFilter,
                                occurrenceIds: new Set(occurrencesToRender.map((occurrence) => occurrence.occurrenceId))
                            })
                        })
                    );
                }

                if (markers.length > 0) {
                    this.registerMarkers('chart-patterns', markers);
                }

                return;
            }

            if (chartPatternDisplayMode === 'pitReplay') {
                const { snapshotsToRender } = getChartPatternSnapshotsForDisplay({
                    summary: this.chartPatternSummary,
                    snapshots: this.chartPatternPitReplaySnapshots,
                    duration,
                    chartPatternDisplayMode,
                    chartPatternKeyFilter,
                    focusedChartPatternOccurrenceId
                });

                const pitReplaySnapshotsToRender = snapshotsToRender.filter((snapshot) =>
                    occurrenceIdsOverride ? occurrenceIdsOverride.has(snapshot.occurrenceId) : true
                );

                const snapshotSeriesToRender = getChartPatternSnapshotSeriesForDisplay({
                    summary: this.chartPatternSummary,
                    snapshots: this.chartPatternPitReplaySnapshots,
                    snapshotSeries: this.chartPatternPitReplaySnapshots,
                    duration,
                    chartPatternDisplayMode,
                    chartPatternKeyFilter,
                    focusedChartPatternOccurrenceId
                }).filter((snapshot) => (occurrenceIdsOverride ? occurrenceIdsOverride.has(snapshot.occurrenceId) : true));

                const markers: SeriesMarker<Time>[] = [];

                if (pitReplaySnapshotsToRender.length > 0) {
                    for (const snapshot of pitReplaySnapshotsToRender) {
                        markers.push(
                            ...this.addChartPatternOverlay(
                                snapshot,
                                {
                                    chartPatternDisplayMode,
                                    isFocusedMode: true
                                },
                                'primary'
                            )
                        );
                    }
                } else if (focusedChartPatternOccurrenceId != null) {
                    const focusedOccurrence = this.chartPatternHistoricalOccurrences.find(
                        (occurrence) => occurrence.occurrenceId === focusedChartPatternOccurrenceId
                    );

                    if (focusedOccurrence && (!occurrenceIdsOverride || occurrenceIdsOverride.has(focusedOccurrence.occurrenceId))) {
                        markers.push(
                            ...this.addChartPatternOverlay(
                                focusedOccurrence,
                                {
                                    chartPatternDisplayMode,
                                    isFocusedMode: true
                                },
                                'primary'
                            )
                        );
                    }
                }

                markers.push(
                    ...(this.chartSettings.shouldRenderChartPatternStateMarkers
                        ? this.buildChartPatternStateMarkers({
                              chartPatternDisplayMode,
                              snapshots: snapshotSeriesToRender
                          })
                        : [])
                );

                if (this.shouldRenderChartPatternEventMarkers) {
                    markers.push(
                        ...this.buildChartPatternEventMarkers({
                            chartPatternDisplayMode,
                            events: getChartPatternHistoricalEventsForDisplay({
                                events: this.chartPatternPitReplayEvents,
                                duration,
                                chartPatternKeyFilter,
                                occurrenceIds:
                                    occurrenceIdsOverride ??
                                    (focusedChartPatternOccurrenceId == null ? undefined : new Set([focusedChartPatternOccurrenceId]))
                            })
                        })
                    );
                }

                if (markers.length > 0) {
                    this.registerMarkers('chart-patterns', markers);
                }

                return;
            }

            const { selectedOccurrenceIds, snapshotsToRender } = getChartPatternSnapshotsForDisplay({
                summary: this.chartPatternSummary,
                snapshots: this.chartPatternSnapshots,
                duration,
                chartPatternDisplayMode,
                chartPatternKeyFilter,
                focusedChartPatternOccurrenceId
            });

            const currentSnapshotsToRender = snapshotsToRender.filter((snapshot) =>
                occurrenceIdsOverride ? occurrenceIdsOverride.has(snapshot.occurrenceId) : true
            );

            if (!currentSnapshotsToRender.length) return;

            const markers: SeriesMarker<Time>[] = [];

            const snapshotSeriesToRender = getChartPatternSnapshotSeriesForDisplay({
                summary: this.chartPatternSummary,
                snapshots: this.chartPatternSnapshots,
                snapshotSeries: this.chartPatternSnapshotSeries,
                duration,
                chartPatternDisplayMode,
                chartPatternKeyFilter,
                focusedChartPatternOccurrenceId
            }).filter((snapshot) => (occurrenceIdsOverride ? occurrenceIdsOverride.has(snapshot.occurrenceId) : true));

            for (const snapshot of currentSnapshotsToRender) {
                const shouldRenderAllFilteredOccurrencesAsPrimary =
                    chartPatternDisplayMode === 'allCurrent' && chartPatternKeyFilter != null;

                const emphasis =
                    chartPatternDisplayMode === 'allCurrent' &&
                    !shouldRenderAllFilteredOccurrencesAsPrimary &&
                    !selectedOccurrenceIds.has(snapshot.occurrenceId)
                        ? 'secondary'
                        : 'primary';

                markers.push(
                    ...this.addChartPatternOverlay(
                        snapshot,
                        {
                            chartPatternDisplayMode,
                            isFocusedMode
                        },
                        emphasis
                    )
                );
            }

            markers.push(
                ...(this.chartSettings.shouldRenderChartPatternStateMarkers
                    ? this.buildChartPatternStateMarkers({
                          chartPatternDisplayMode,
                          snapshots: snapshotSeriesToRender
                      })
                    : [])
            );

            if (this.shouldRenderChartPatternEventMarkers) {
                const eventMarkers = this.buildChartPatternEventMarkers({
                    chartPatternDisplayMode,
                    events: getChartPatternEventsForDisplay({
                        summary: this.chartPatternSummary,
                        duration,
                        chartPatternKeyFilter,
                        occurrenceIds: new Set(currentSnapshotsToRender.map((snapshot) => snapshot.occurrenceId))
                    })
                });

                markers.push(...eventMarkers);
            }

            if (markers.length > 0) {
                this.registerMarkers('chart-patterns', markers);
            }
        } catch (error) {
            console.error('Error fetching chart patterns:', error);
        }
    }

    removeChartPatterns() {
        if (this.candlestickSeries) {
            for (const primitive of this.chartPatternPrimitives.values()) {
                this.candlestickSeries.detachPrimitive(primitive);
            }
        }

        for (const lineSeries of this.chartPatternSeries.values()) {
            this.chart?.removeSeries(lineSeries);
        }

        this.chartPatternSeries.clear();
        this.chartPatternPrimitives.clear();
        this.clearMarkerSource('chart-patterns');
    }

    removeCandlestickPatterns() {
        this.clearMarkerSource('candlestick-patterns');
    }

    async fetchKeyLevels() {
        const { entity, startDateTime, endDateTime, shouldRemoveAfterHours } = this.chartSettings;

        if (this.keyLevels?.[0]?.barDuration === this.chartSettings.duration) return;
        this.keyLevels = [];

        try {
            const keyLevelsResult = await trpc.entity.fetchKeyLevels.query({
                entityId: entity.id,
                entityType: entity.entityType,
                startDateTime,
                endDateTime,
                duration: this.chartSettings.duration,
                shouldRemoveAfterHours
            });

            if (!keyLevelsResult?.success) {
                console.error('Failed to fetch key levels:', keyLevelsResult?.errorMessages);
                return;
            }

            this.keyLevels = keyLevelsResult.keyLevels;
        } catch (error) {
            console.error('Error fetching key levels:', error);
        }
    }

    addChartTooltipMarkers(markers: ChartTextItem[]) {
        const { container, chart, candlestickSeries } = this;
        if (!chart || !candlestickSeries) return;

        const markerMap: Map<Time, ChartTextItem[]> = new Map();

        // Create visual indicators (dots) for bars with tooltips
        const seriesMarkers: SeriesMarker<Time>[] = [];

        for (const marker of markers) {
            const time = convertEstTimestampToTradingView(getUnixTime(marker.dateTime));

            const existingMarkers = markerMap.get(time) || [];
            existingMarkers.push(marker);
            markerMap.set(time, existingMarkers);

            // Add a visual indicator for this bar
            seriesMarkers.push({
                time,
                position: 'aboveBar',
                color: '#FFEB00', // Yellow dot (more visible)
                shape: 'circle',
                size: 1.5
            });
        }

        // Add the visual indicators to the candlestick series
        if (candlestickSeries && seriesMarkers.length > 0) {
            createSeriesMarkers(candlestickSeries, seriesMarkers);
        }

        // Margin for tooltip positioning (used in previous implementation)

        // Create and style the tooltip html element
        const toolTip = document.createElement('div');

        // Set initial styles for the tooltip
        toolTip.style.position = 'fixed'; // Use fixed positioning to follow mouse
        toolTip.style.display = 'none';
        toolTip.style.padding = '8px';
        toolTip.style.boxSizing = 'border-box';
        toolTip.style.fontSize = '12px';
        toolTip.style.textAlign = 'left';
        toolTip.style.zIndex = '999';
        toolTip.style.pointerEvents = 'none';
        toolTip.style.border = '1px solid #4d4d4d';
        toolTip.style.borderRadius = '4px';
        toolTip.style.fontFamily = 'sans-serif';
        toolTip.style.lineHeight = '18px';
        toolTip.style.fontWeight = '300';
        toolTip.style.background = '#333333';
        toolTip.style.color = 'white';
        toolTip.style.boxShadow = '0 2px 5px rgba(0,0,0,0.3)';
        container.appendChild(toolTip);

        // update tooltip

        chart.subscribeCrosshairMove((param) => {
            if (
                param.point === undefined ||
                !param.time ||
                param.point.x < 0 ||
                param.point.x > container.clientWidth ||
                param.point.y < 0 ||
                param.point.y > container.clientHeight
            ) {
                toolTip.style.display = 'none';
            } else {
                const data = param.seriesData.get(candlestickSeries) as BarData<Time> | undefined;
                const time = data?.time;

                if (!time) return;

                const format = this.chartSettings.duration === '1d' ? 'ymd' : 'ymdhms';
                const barDateTime = getDateFromUnix(convertTradingViewTimestampToEst(time as UTCTimestamp), format);
                let markerText = `${barDateTime}<br>`;
                const markers = markerMap.get(time) || [];

                for (const marker of markers) {
                    markerText += `${marker.text}<br>`;
                }

                const barIndex = this.barsTimestampMap.get(getUnixTime(barDateTime));
                if (barIndex) markerText += `<br>${barIndex}`;

                toolTip.innerHTML = `<div>${markerText}</div>`;

                // Measure width and height after setting content
                const tooltipWidth = toolTip.offsetWidth;
                const tooltipHeight = toolTip.offsetHeight;
                // Get the mouse position in client coordinates (relative to the viewport)
                const rect = container.getBoundingClientRect();
                // Convert chart-relative coordinates to client coordinates
                const clientX = rect.left + param.point.x;
                const clientY = rect.top + param.point.y;

                // Position tooltip directly at the mouse cursor with a small offset
                // and ensure it stays within chart boundaries
                const shiftedCoordinate = Math.max(5, Math.min(window.innerWidth - tooltipWidth - 5, clientX + 10));

                // Position tooltip above the mouse cursor
                const coordinateY = Math.max(5, Math.min(window.innerHeight - tooltipHeight - 5, clientY - tooltipHeight - 10));

                toolTip.style.display = 'block';
                toolTip.style.left = `${shiftedCoordinate}px`;
                toolTip.style.top = `${coordinateY}px`;
            }
        });
    }

    addChartConsoleItems(items: ChartTextItem[]) {
        const { container, chart, candlestickSeries } = this;
        if (!chart || !candlestickSeries) return;

        const outputConsole = document.getElementById('outputConsole');
        if (!outputConsole) return;

        // Group items by timestamp
        const itemMap: Map<Time, ChartTextItem[]> = new Map();

        for (const item of items) {
            const time = convertEstTimestampToTradingView(getUnixTime(item.dateTime));

            const existingItems = itemMap.get(time) || [];
            existingItems.push(item);
            itemMap.set(time, existingItems);
        }

        // Create a container for the items
        const itemsContainer = document.createElement('div');
        itemsContainer.style.display = 'flex';
        itemsContainer.style.flexWrap = 'wrap';
        itemsContainer.style.gap = '8px';
        outputConsole.appendChild(itemsContainer);

        // Subscribe to crosshair movement
        chart.subscribeCrosshairMove((param) => {
            if (
                param.point === undefined ||
                !param.time ||
                param.point.x < 0 ||
                param.point.x > container.clientWidth ||
                param.point.y < 0 ||
                param.point.y > container.clientHeight
            ) {
                // Just clear the content but don't hide the container
                // This prevents the flickering effect
                itemsContainer.innerHTML = '';
            } else {
                const data = param.seriesData.get(candlestickSeries) as BarData<Time> | undefined;
                const time = data?.time;

                if (!time) {
                    // Just clear the content but don't hide the container
                    itemsContainer.innerHTML = '';
                    return;
                }

                const timeItems = itemMap.get(time) || [];

                itemsContainer.innerHTML = '';

                if (timeItems.length > 0) {
                    // We don't need to set display: block here anymore since
                    // the container is already visible when there are console items
                    // outputConsole.style.display = 'block';

                    for (const item of timeItems) {
                        const itemDiv = document.createElement('div');
                        itemDiv.classList.add('console-item');
                        itemDiv.style.padding = '6px 12px';
                        itemDiv.style.borderRadius = '4px';
                        itemDiv.style.backgroundColor = '#333';
                        itemDiv.style.color = '#fff';
                        itemDiv.style.fontSize = '12px';
                        itemDiv.innerHTML = item.text;

                        itemsContainer.appendChild(itemDiv);
                    }
                } else {
                    // Hide the content but keep the container visible
                    // This prevents the flickering effect
                    itemsContainer.innerHTML = '';
                }
            }
        });
    }

    // Add a price line to the chart
    addPriceLine(line: PriceLine) {
        if (!this.chart) return;
        const lineStartingBarIndex = this.barsTimestampMap.get(line.startTimestamp);
        const lineEndingBarIndex = this.barsTimestampMap.get(line.endTimestamp);

        if (lineStartingBarIndex === undefined || lineEndingBarIndex === undefined) return;

        const lineSeries = this.chart.addSeries(LineSeries, {
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: false,
            lineWidth: 1,
            color: line.color
        });

        const data: SingleValueData[] = [];

        for (let i = lineStartingBarIndex; i <= lineEndingBarIndex; i++) {
            const bar = this.bars[i];

            data.push({ time: convertEstTimestampToTradingView(bar.t), value: line.price });
        }

        lineSeries.setData(data);
    }

    /**
     * Create comparison charts when comparison data is available
     * This creates three charts:
     * 1. Comparison Chart - shows normalized prices of both assets
     * 2. Relative Performance Chart - shows cumulative relative performance
     * 3. Anchored Performance Chart - shows relative performance over different lookback periods
     */
    createComparisonCharts() {
        if (!this.comparisonData) {
            console.warn('No comparison data available');
            return;
        }

        // Create container for comparison charts
        let comparisonChartsContainer = document.querySelector('#comparison-charts-container') as HTMLElement;

        // Create container if it doesn't exist
        if (!comparisonChartsContainer) {
            comparisonChartsContainer = document.createElement('div');
            comparisonChartsContainer.id = 'comparison-charts-container';
            comparisonChartsContainer.style.width = '100%';
            comparisonChartsContainer.style.marginTop = '20px';

            // Insert after the main chart container
            this.container.parentNode?.insertBefore(comparisonChartsContainer, this.container.nextSibling);
        } else {
            // Clear existing content
            comparisonChartsContainer.innerHTML = '';
        }

        // Get the symbols for headers
        const primarySymbol = this.comparisonData.metadata.primaryAsset.symbol;
        const comparisonSymbol = this.comparisonData.metadata.comparisonAsset.symbol;

        // Create containers for each chart with wrapper for header and close button
        // Split into smaller functions to avoid linting issues with too many statements
        const createChartHeader = (id: string, title: string) => {
            const headerDiv = document.createElement('div');
            headerDiv.style.display = 'flex';
            headerDiv.style.justifyContent = 'space-between';
            headerDiv.style.alignItems = 'center';
            headerDiv.style.marginBottom = '5px';
            headerDiv.style.padding = '0 5px';

            // Add title
            const titleSpan = document.createElement('span');
            titleSpan.textContent = title;
            titleSpan.style.fontWeight = 'bold';
            titleSpan.style.color = '#DDD';
            headerDiv.appendChild(titleSpan);

            // Add close button
            const closeButton = document.createElement('button');
            closeButton.innerHTML = '&times;';
            closeButton.style.background = 'none';
            closeButton.style.border = 'none';
            closeButton.style.color = '#DDD';
            closeButton.style.fontSize = '20px';
            closeButton.style.cursor = 'pointer';
            closeButton.style.padding = '0 5px';
            closeButton.title = `Remove ${title}`;
            closeButton.onclick = () => this.removeComparisonChart(id);
            headerDiv.appendChild(closeButton);

            return headerDiv;
        };

        const createChartContainer = (id: string, title: string, height: string) => {
            // Create wrapper div for the chart and its header
            const chartWrapper = document.createElement('div');
            chartWrapper.id = `${id}-wrapper`;
            chartWrapper.style.width = '100%';
            chartWrapper.style.marginBottom = '20px';
            chartWrapper.style.position = 'relative';

            // Add header with title and close button
            const headerDiv = createChartHeader(id, title);
            chartWrapper.appendChild(headerDiv);

            // Create the actual chart container
            const chartContainer = document.createElement('div');
            chartContainer.id = id;
            chartContainer.style.width = '100%';
            chartContainer.style.height = height;
            chartWrapper.appendChild(chartContainer);

            return { wrapper: chartWrapper, container: chartContainer };
        };

        // Create the three chart containers
        const comparisonChart = createChartContainer(
            'comparison-chart',
            `Price Comparison: ${primarySymbol} vs ${comparisonSymbol}`,
            '300px'
        );

        const performanceChart = createChartContainer(
            'performance-chart',
            `Relative Performance: ${primarySymbol} vs ${comparisonSymbol}`,
            '200px'
        );

        const anchoredChart = createChartContainer(
            'anchored-performance-chart',
            `Anchored Relative Performance: ${primarySymbol} vs ${comparisonSymbol}`,
            '200px'
        );

        // Add containers to the main container
        comparisonChartsContainer.appendChild(comparisonChart.wrapper);
        comparisonChartsContainer.appendChild(performanceChart.wrapper);

        // Add period selector checkboxes for anchored performance chart
        const periodSelectorContainer = document.createElement('div');
        periodSelectorContainer.style.display = 'flex';
        periodSelectorContainer.style.marginBottom = '10px';
        periodSelectorContainer.style.gap = '10px';
        periodSelectorContainer.style.flexWrap = 'wrap';

        // Default periods
        const defaultPeriods = DEFAULT_COMPARISON_LOOKBACK_BARS;
        // Selected periods state
        this.selectedAnchoredPeriods = [...defaultPeriods];

        // Define period colors here to match the ones used in createAnchoredPerformanceChart
        const periodColors: Record<number, string> = {
            7: 'rgb(255, 99, 71)', // Tomato red
            14: 'rgb(255, 215, 0)', // Gold
            30: 'rgb(40, 134, 134)',
            60: 'rgb(134, 40, 134)',
            90: 'rgb(134, 134, 40)',
            120: 'rgb(134, 80, 40)'
        };

        // Create period checkbox function
        const createPeriodCheckbox = (period: number) => {
            const checkboxContainer = document.createElement('label');
            checkboxContainer.style.display = 'flex';
            checkboxContainer.style.alignItems = 'center';
            checkboxContainer.style.marginRight = '10px';
            checkboxContainer.style.color = '#DDD';
            checkboxContainer.style.cursor = 'pointer';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.checked = true;
            checkbox.dataset.period = period.toString();
            checkbox.style.marginRight = '5px';
            checkbox.className = 'period-selector';

            checkbox.addEventListener('change', () => {
                if (checkbox.checked) {
                    // Add period to selected periods if not already there
                    if (!this.selectedAnchoredPeriods.includes(period)) {
                        this.selectedAnchoredPeriods.push(period);
                    }
                } else {
                    // Remove period from selected periods
                    const index = this.selectedAnchoredPeriods.indexOf(period);

                    if (index !== -1) {
                        this.selectedAnchoredPeriods.splice(index, 1);
                    }
                }

                // Update chart with selected periods
                this.createAnchoredPerformanceChart(anchoredChart.container, this.selectedAnchoredPeriods);

                this.syncAllCharts();
            });

            checkboxContainer.appendChild(checkbox);

            // Create a text span with the color matching the line series
            const textSpan = document.createElement('span');
            textSpan.textContent = `${period} bars`;
            textSpan.style.color = periodColors[period] || '#DDD';
            checkboxContainer.appendChild(textSpan);

            return checkboxContainer;
        };

        // Add period checkboxes
        defaultPeriods.forEach((period) => {
            periodSelectorContainer.appendChild(createPeriodCheckbox(period));
        });

        comparisonChartsContainer.appendChild(periodSelectorContainer);
        comparisonChartsContainer.appendChild(anchoredChart.wrapper);

        // Create the three comparison charts
        this.createComparisonChart(comparisonChart.container, primarySymbol, comparisonSymbol);
        this.createPerformanceChart(performanceChart.container, primarySymbol, comparisonSymbol);
        this.createAnchoredPerformanceChart(anchoredChart.container, this.selectedAnchoredPeriods);
    }

    /**
     * Remove a specific comparison chart
     */
    removeComparisonChart(chartId: string) {
        const chartKey = chartId === 'anchored-performance-chart' ? 'anchored-chart' : chartId;

        // Get the chart from subCharts
        const chart = this.subCharts.get(chartKey);
        if (!chart) return;

        // Unsubscribe events and remove the chart
        this.unsubscribeChartEvents(chart);
        chart.remove();
        this.subCharts.delete(chartKey);
        this.unregisterChartContainer(chartKey);

        // Remove any series associated with this chart
        const keysToRemove: string[] = [];

        this.subChartSeries.forEach((_, key) => {
            if (
                key.startsWith(chartId) ||
                key.includes(chartId) ||
                (chartId === 'anchored-performance-chart' && key.startsWith('anchored-'))
            ) {
                keysToRemove.push(key);
            }
        });

        keysToRemove.forEach((key) => {
            this.subChartSeries.delete(key);
        });

        // Remove the chart wrapper from DOM
        const wrapper = document.getElementById(`${chartId}-wrapper`);

        if (wrapper) {
            wrapper.remove();
        }

        // If this was the anchored performance chart, also remove the period selector
        if (chartId === 'anchored-performance-chart') {
            const periodSelector = document.querySelector('div:has(.period-selector)');

            if (periodSelector) {
                periodSelector.remove();
            }
        }

        // Re-sync remaining charts
        this.syncAllCharts();
    }

    /**
     * Create a chart comparing the normalized prices of both assets
     */

    createComparisonChart(container: HTMLElement, primarySymbol: string, comparisonSymbol: string) {
        if (!this.comparisonData?.comparisonData) {
            console.warn('No comparison data available for comparison chart');
            return;
        }

        // Clear any existing content in the container
        container.innerHTML = '';

        // Create a wrapper div that will contain both the chart and its legend
        const chartWrapper = document.createElement('div');
        chartWrapper.style.position = 'relative';
        chartWrapper.style.width = '100%';
        chartWrapper.style.height = '100%';
        container.appendChild(chartWrapper);

        const comparisonChart = createChart(chartWrapper, {
            layout: {
                background: { color: '#222' },
                textColor: '#DDD'
            },
            grid: {
                vertLines: { color: '#444' },
                horzLines: { color: '#444' }
            },
            timeScale: {
                timeVisible: true,
                secondsVisible: false
            },
            width: this.getResolvedChartWidthForContainer(chartWrapper)
        });

        // Add to subCharts for cleanup later
        this.subCharts.set('comparison-chart', comparisonChart);
        this.registerChartContainer('comparison-chart', chartWrapper);

        // Add legend to chart wrapper
        const legend = document.createElement('div');
        legend.style.position = 'absolute';
        legend.style.left = '12px';
        legend.style.top = '12px';
        legend.style.zIndex = '5';
        legend.style.fontSize = '14px';
        legend.style.fontWeight = 'bold';
        legend.style.color = '#DDD';
        legend.textContent = `Price Comparison: ${primarySymbol} vs ${comparisonSymbol}`;
        chartWrapper.appendChild(legend);

        // Add primary entity series
        const primarySeries = comparisonChart.addSeries(LineSeries, {
            crosshairMarkerVisible: true,
            lastValueVisible: true,
            priceLineVisible: false,
            lineWidth: 2,
            color: 'rgb(0, 120, 255)',
            title: primarySymbol
        });

        // Add comparison entity series
        const comparisonSeries = comparisonChart.addSeries(LineSeries, {
            crosshairMarkerVisible: true,
            lastValueVisible: true,
            priceLineVisible: false,
            lineWidth: 2,
            color: 'rgb(255, 128, 0)',
            title: comparisonSymbol
        });

        // Save series for later reference
        this.subChartSeries.set('comparison-primary', primarySeries);
        this.subChartSeries.set('comparison-secondary', comparisonSeries);

        const primaryData = this.comparisonData.comparisonData.map((bar) => ({
            time: convertEstTimestampToTradingView(bar.t),
            value: bar.eA.nc
        }));

        const comparisonData = this.comparisonData.comparisonData.map((bar) => ({
            time: convertEstTimestampToTradingView(bar.t),
            value: bar.eB.nc
        }));

        primarySeries.setData(primaryData);
        comparisonSeries.setData(comparisonData);

        comparisonChart.timeScale().fitContent();
        this.scheduleChartResize(true);
    }

    /**
     * Create a chart showing the cumulative relative performance
     */
    createPerformanceChart(container: HTMLElement, primarySymbol: string, comparisonSymbol: string) {
        if (!this.comparisonData?.cumulativePerformance) {
            console.warn('No comparison data available for performance chart');
            return;
        }

        // Clear any existing content in the container
        container.innerHTML = '';

        // Create a wrapper div that will contain both the chart and its legend
        const chartWrapper = document.createElement('div');
        chartWrapper.style.position = 'relative';
        chartWrapper.style.width = '100%';
        chartWrapper.style.height = '100%';
        container.appendChild(chartWrapper);

        const performanceChart = createChart(chartWrapper, {
            layout: {
                background: { color: '#222' },
                textColor: '#DDD'
            },
            grid: {
                vertLines: { color: '#444' },
                horzLines: { color: '#444' }
            },
            timeScale: {
                timeVisible: true,
                secondsVisible: false
            },
            width: this.getResolvedChartWidthForContainer(chartWrapper)
        });

        // Add to subCharts for cleanup later
        this.subCharts.set('performance-chart', performanceChart);
        this.registerChartContainer('performance-chart', chartWrapper);

        // Add legend to chart wrapper
        const legend = document.createElement('div');
        legend.style.position = 'absolute';
        legend.style.left = '12px';
        legend.style.top = '12px';
        legend.style.zIndex = '5';
        legend.style.fontSize = '14px';
        legend.style.fontWeight = 'bold';
        legend.style.color = '#DDD';
        legend.textContent = `Relative Performance: ${primarySymbol} vs ${comparisonSymbol}`;
        chartWrapper.appendChild(legend);

        // Add primary entity series
        const performanceSeries = performanceChart.addSeries(LineSeries, {
            crosshairMarkerVisible: true,
            lastValueVisible: true,
            priceLineVisible: false,
            lineWidth: 2,
            color: 'rgb(40, 134, 134)',
            title: 'Relative Performance'
        });

        // Save series for later reference
        this.subChartSeries.set('performance-series', performanceSeries);

        const performanceData = this.comparisonData.comparisonData.map((d) => ({
            time: convertEstTimestampToTradingView(d.t),
            value: d.c.rp
        }));

        performanceSeries.setData(performanceData);

        // Add a zero line to better visualize over/under performance
        const zeroLine = performanceChart.addSeries(LineSeries, {
            color: 'rgba(255, 255, 255, 0.3)',
            lineWidth: 1,
            lastValueVisible: false,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
            title: 'Zero'
        });

        // Add zero line if we have data
        if (performanceData.length > 0) {
            const firstPoint = performanceData[0];
            const lastPoint = performanceData[performanceData.length - 1];

            zeroLine.setData([
                { time: firstPoint.time, value: 0 },
                { time: lastPoint.time, value: 0 }
            ]);
        }

        performanceChart.timeScale().fitContent();
        this.scheduleChartResize(true);
    }

    /**
     * Create a chart showing the anchored relative performance over different lookback periods
     */
    createAnchoredPerformanceChart(container: HTMLElement, selectedPeriods: number[] = DEFAULT_COMPARISON_LOOKBACK_BARS) {
        if (!this.comparisonData?.anchoredPerformance) {
            console.warn('No comparison data available for anchored performance chart');
            return;
        }

        // Clear any existing content in the container
        container.innerHTML = '';

        // First remove the old chart if it exists
        const existingChart = this.subCharts.get('anchored-chart');

        if (existingChart) {
            // Unsubscribe any event listeners
            this.unsubscribeChartEvents(existingChart);
            // Remove the chart
            existingChart.remove();
            this.subCharts.delete('anchored-chart');

            // Also clean up any series associated with this chart
            const keysToRemove: string[] = [];

            this.subChartSeries.forEach((_, key) => {
                if (key.startsWith('anchored-period')) {
                    keysToRemove.push(key);
                }
            });

            keysToRemove.forEach((key) => {
                this.subChartSeries.delete(key);
            });
        }

        // Check if we have anchored performance data
        if (this.comparisonData.anchoredPerformance.length === 0) {
            container.innerHTML = '<div style="color: #DDD; padding: 20px;">No anchored performance data available</div>';
            return;
        }

        // Check if all anchored performance values are zero
        let isAllZeros = true;

        for (const dataPoint of this.comparisonData.anchoredPerformance) {
            for (const period of dataPoint.ps) {
                if (period.rp !== 0) {
                    isAllZeros = false;
                    break;
                }
            }

            if (!isAllZeros) break;
        }

        if (isAllZeros) {
            container.innerHTML =
                '<div style="color: #DDD; padding: 20px;">No meaningful anchored performance data available. All values are zero.</div>';

            return;
        }

        // Get available periods from the data
        const availablePeriods = new Set<number>();

        this.comparisonData.anchoredPerformance.forEach((dataPoint) => {
            dataPoint.ps.forEach((period) => {
                availablePeriods.add(period.p);
            });
        });

        // Filter to only include available periods
        const filteredPeriods = selectedPeriods.filter((period) => availablePeriods.has(period));

        if (filteredPeriods.length === 0) {
            container.innerHTML =
                '<div style="color: #DDD; padding: 20px;">None of the selected lookback periods are available in the data</div>';

            return;
        }

        // Create a wrapper div that will contain both the chart and its legend
        const chartWrapper = document.createElement('div');
        chartWrapper.style.position = 'relative';
        chartWrapper.style.width = '100%';
        chartWrapper.style.height = '100%';
        container.appendChild(chartWrapper);

        const anchoredChart = createChart(chartWrapper, {
            layout: {
                background: { color: '#222' },
                textColor: '#DDD'
            },
            grid: {
                vertLines: { color: '#444' },
                horzLines: { color: '#444' }
            },
            timeScale: {
                timeVisible: true,
                secondsVisible: false
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.2,
                    bottom: 0.2
                }
            },
            width: this.getResolvedChartWidthForContainer(chartWrapper)
        });

        // Add to subCharts for cleanup later
        this.subCharts.set('anchored-chart', anchoredChart);
        this.registerChartContainer('anchored-chart', chartWrapper);

        // Add legend to chart wrapper
        const legend = document.createElement('div');
        legend.style.position = 'absolute';
        legend.style.left = '12px';
        legend.style.top = '12px';
        legend.style.zIndex = '5';
        legend.style.fontSize = '14px';
        legend.style.fontWeight = 'bold';
        legend.style.color = '#DDD';
        legend.textContent = `Anchored Relative Performance: ${this.comparisonData.metadata.primaryAsset.symbol} vs ${this.comparisonData.metadata.comparisonAsset.symbol}`;
        chartWrapper.appendChild(legend);

        // Different colors for each period
        const periodColors: Record<number, string> = {
            7: 'rgb(255, 99, 71)', // Tomato red
            14: 'rgb(255, 215, 0)', // Gold
            30: 'rgb(40, 134, 134)',
            60: 'rgb(134, 40, 134)',
            90: 'rgb(134, 134, 40)',
            120: 'rgb(134, 80, 40)'
        };

        // Create a zero line
        const zeroLine = anchoredChart.addSeries(LineSeries, {
            color: 'rgba(238, 255, 0, 0.5)',
            lineWidth: 1,
            lastValueVisible: false,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
            title: 'Zero'
        });

        // Create zero line data (need at least two points)
        if (this.comparisonData.anchoredPerformance.length > 0) {
            const firstPoint = this.comparisonData.anchoredPerformance[0];
            const lastPoint = this.comparisonData.anchoredPerformance[this.comparisonData.anchoredPerformance.length - 1];

            if (firstPoint && lastPoint) {
                const zeroLineData = [
                    {
                        time: convertEstTimestampToTradingView(firstPoint.t),
                        value: 0
                    },
                    {
                        time: convertEstTimestampToTradingView(lastPoint.t),
                        value: 0
                    }
                ];

                zeroLine.setData(zeroLineData);
            }
        }

        // Create series for each period
        const anchoredSeries: Record<string, ISeriesApi<'Line'>> = {};

        filteredPeriods.forEach((period) => {
            const seriesName = `period${period}`;

            anchoredSeries[seriesName] = anchoredChart.addSeries(LineSeries, {
                crosshairMarkerVisible: true,
                lastValueVisible: true,
                priceLineVisible: false,
                lineWidth: 2,
                color: periodColors[period] || 'rgb(100, 100, 100)',
                title: `${period} bars`
            });

            // Save series for later reference
            this.subChartSeries.set(`anchored-${seriesName}`, anchoredSeries[seriesName]);
        });

        // Collect data for each period
        const seriesData: Record<string, SingleValueData<Time>[]> = {};

        filteredPeriods.forEach((period) => {
            seriesData[`period${period}`] = [];
        });

        // Map the data to the correct format
        this.comparisonData.anchoredPerformance.forEach((dataPoint) => {
            const time = convertEstTimestampToTradingView(dataPoint.t);

            dataPoint.ps.forEach((periodData) => {
                const seriesName = `period${periodData.p}`;

                // Only include filtered periods
                if (filteredPeriods.includes(periodData.p)) {
                    // Convert to percentage for better readability
                    seriesData[seriesName]?.push({
                        time,
                        value: (periodData.rp ?? 0) * 100
                    });
                }
            });
        });

        // Set data for each series
        filteredPeriods.forEach((period) => {
            const seriesName = `period${period}`;
            const seriesDataPoints = seriesData[seriesName];

            if (seriesDataPoints && seriesDataPoints.length > 0) {
                anchoredSeries[seriesName].setData(seriesDataPoints);
            }
        });

        anchoredChart.timeScale().fitContent();
        this.scheduleChartResize(true);
    }

    destroy(): void {
        if (this.resizeFrameId != null) {
            window.cancelAnimationFrame(this.resizeFrameId);
            this.resizeFrameId = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        } else {
            window.removeEventListener('resize', this.handleWindowResize);
        }

        if (this.chart) {
            this.unsubscribeChartEvents(this.chart);
        }

        this.removeAllSubCharts();

        if (this.chart && !this.isChartDisposed(this.chart)) {
            this.chart.remove();
        }

        this.chart = undefined;
        this.candlestickSeries = undefined;
        this.volumeSeries = undefined;
        this.subCharts.clear();
        this.subChartSeries.clear();
        this.chartContainers.clear();
        this.lineSeriesMap.clear();
        this.chartPatternPrimitives.clear();
        this.markersBySource.clear();

        const comparisonChartContainer = document.getElementById('comparison-charts-container');
        if (comparisonChartContainer) comparisonChartContainer.remove();
    }
}

export default ChartHelper;
